import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { getIdToken } from 'firebase/auth';
import { db, auth } from '@/lib/firebase';
import { MerchantApplicationService } from '../services/merchantApplicationService';
import { MerchantApplication, PipelineStatus, ProcessingHistory, BeneficialOwner, Pricing, CorporationInfo, PersonalInfo, IntegrationLogEntry } from '../types';
import { useMerchantApplicationSettings } from '../hooks/useMerchantApplicationSettings';
import { useIndustryClassifications } from '../hooks/useIndustryClassifications';
import type { ApplicationSource } from '../types/fieldSettings';
import { SicCodeSelector } from '@/components/ui/sic-code-selector';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Building,
  Building2,
  Phone,
  MapPin,
  Globe,
  Calendar,
  FileText,
  Shield,
  Landmark,
  Clock,
  Mail,
  FileIcon,
  Trash2,
  Plus,
  DollarSign,
  CreditCard,
  TrendingUp,
  Percent,
  Zap,
  AlertTriangle,
  AlertCircle,
  Hash,
  Send,
  Eye,
  XCircle,
  Info,
  Download,
  Save,
  Copy,
  X,
  Key,
  RefreshCw,
  Loader2,
  Activity,
  RotateCcw,
  Receipt,
  CalendarDays,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { cn } from '@/lib/utils';
import { CommunicationsSection } from './CommunicationsSection';
import { BeneficialOwnersDisplay } from './BeneficialOwnersDisplay';
import { BankDetailsDisplay } from '@/features/banking-partners/components/BankDetailsDisplay';
import { bankingPartnerService } from '@/features/banking-partners/services/bankingPartnerService';
import { payarcService } from '@/services/payarcService';
import { DocumentsViewer } from './DocumentsViewer';
import { SendToPayArcButton } from './SendToPayArcButton';
import { AdditionalDocumentRequest } from './AdditionalDocumentRequest';
import { ProcessingAnalyticsSection } from './ProcessingAnalyticsSection';

// Corporation Types for dropdown
const CORPORATION_TYPES = [
  { value: "sole_proprietorship", label: "Sole Proprietorship" },
  { value: "llc", label: "Limited Liability Company (LLC)" },
  { value: "s_corp", label: "S Corporation" },
  { value: "c_corp", label: "C Corporation" },
  { value: "partnership", label: "Partnership" },
  { value: "lp", label: "Limited Partnership (LP)" },
  { value: "llp", label: "Limited Liability Partnership (LLP)" },
  { value: "non_profit", label: "Non-Profit Organization" },
];

interface BankingPartner {
  id: string;
  name: string;
  color: string;
}

const formatPhoneNumber = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 3) {
    return `(${numbers}`;
  }
  if (numbers.length <= 6) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  }
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
};

const formatWebsite = (value: string): string => {
  if (!value) return value;
  if (!/^https?:\/\//i.test(value)) {
    return `https://${value}`;
  }
  return value;
};

const PIPELINE_COLORS: Record<PipelineStatus, string> = {
  lead: '#6B7280',
  phone: '#3B82F6',
  offer: '#6366F1',
  underwriting: '#F59E0B',
  documents: '#8B5CF6',
  approved: '#10B981',
  declined: '#EF4444',
  onboarded: '#059669',
  live: '#22C55E',
  processing: '#0EA5E9',
  failed_submission: '#EC4899',
};

export function MerchantApplicationDetail() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const [editMode, setEditMode] = useState<{ [key: string]: boolean }>({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState<string | undefined>(undefined);
  const [isPayArcSending, setIsPayArcSending] = useState(false);
  const [payArcResponse, setPayArcResponse] = useState<unknown>(null);
  const [isExamineDialogOpen, setIsExamineDialogOpen] = useState(false);
  const [payArcPayloadPreview, setPayArcPayloadPreview] = useState<{ payload: unknown; errors: string[] } | null>(null);
  const [editedPayloadText, setEditedPayloadText] = useState<string>('');
  const [isSendingCustom, setIsSendingCustom] = useState(false);
  const [selectedLog, setSelectedLog] = useState<IntegrationLogEntry | null>(null);
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false);
  const [isSubmittingPayArc, setIsSubmittingPayArc] = useState(false);
  const [isExamineSubmissionDialogOpen, setIsExamineSubmissionDialogOpen] = useState(false);
  const [submissionPayloadPreview, setSubmissionPayloadPreview] = useState<{ payload: unknown; errors: string[] } | null>(null);
  const [editedSubmissionPayloadText, setEditedSubmissionPayloadText] = useState<string>('');
  const [isSendingCustomSubmission, setIsSendingCustomSubmission] = useState(false);
  const [isRetrievingBearerKey, setIsRetrievingBearerKey] = useState(false);
  const [isBusinessDescriptionModalOpen, setIsBusinessDescriptionModalOpen] = useState(false);
  const [businessDescriptionValue, setBusinessDescriptionValue] = useState<string>('');



  // Fetch application
  const { data: application, isLoading } = useQuery({
    queryKey: ['merchantApplication', applicationId],
    queryFn: () => MerchantApplicationService.getApplication(applicationId!),
    enabled: !!applicationId,
  });
  
  // Tenant-configured field visibility for the application source
  const appSource = (application?.applicationSource || (application?.data as any)?.applicationSource || 'InvoiceBug') as ApplicationSource;
  const { shouldShowField: _shouldShowField, getFieldDefaultValue, sourceSettings } = useMerchantApplicationSettings(appSource);

  // Load industry classifications for SIC code selector
  const { allEnabledCodes, loading: industryCodesLoading } = useIndustryClassifications(appSource);

  const shouldShowField = (path: string): boolean => {
    const aliasMap: Record<string, string> = {
      'corporationInfo.businessType': 'corporationInfo.corporationType',
    };
    return _shouldShowField(aliasMap[path] || path);
  };
  const isLinkEnabled = (path: string): boolean => {
    const setting = sourceSettings?.fieldSettings?.[path];
    if (!setting) return true;
    return setting.linkEnabled !== false;
  };

  // Helper to hydrate application data with defaults for hidden fields
  const hydrateWithDefaults = (data: any) => {
    if (!data) return data;
    const result: any = JSON.parse(JSON.stringify(data));

    // Personal Info
    ['firstName','lastName','email','phone'].forEach((f) => {
      const path = `personalInfo.${f}`;
      if (!shouldShowField(path)) {
        const defVal = getFieldDefaultValue?.(path);
        if (defVal !== undefined) {
          result.personalInfo = result.personalInfo || {};
          result.personalInfo[f] = defVal;
        }
      }
    });

    // Corporation Info (simple)
    [
      'businessName','corporationType','businessStartDate','ein','dba','website','businessDescription','sicCode','sicDescription','customerServiceEmail','customerServicePhone'
    ].forEach((f) => {
      const path = `corporationInfo.${f}`;
      if (!shouldShowField(path)) {
        const defVal = getFieldDefaultValue?.(path);
        if (defVal !== undefined) {
          result.corporationInfo = result.corporationInfo || {};
          result.corporationInfo[f] = defVal;
        }
      }
    });

    // Corporation Info (address)
    ;['street','city','state','zipCode','fullAddress'].forEach((sub) => {
      const path = `corporationInfo.businessAddress.${sub}`;
      if (!shouldShowField(path)) {
        const defVal = getFieldDefaultValue?.(path);
        if (defVal !== undefined) {
          result.corporationInfo = result.corporationInfo || {};
          result.corporationInfo.businessAddress = result.corporationInfo.businessAddress || {};
          result.corporationInfo.businessAddress[sub] = defVal;
        }
      }
    });

    // Processing History
    ;[
      'isCurrentlyProcessing','monthlyVolume','averageTicket','highTicket','salesDeliveryDays','whenIsCustomerCharged','orderTurnaroundTime','advancedDeposits','depositPct','currentProcessor','cardPresentPercentage','ecommercePercentage','acceptOfflineCards','retailPercentage','onlinePercentage','b2bPercentage','b2cPercentage','bankruptcy','hasBeenTerminated','terminationExplanation'
    ].forEach((f) => {
      const path = `processingHistory.${f}`;
      if (!shouldShowField(path)) {
        const defVal = getFieldDefaultValue?.(path);
        if (defVal !== undefined) {
          result.processingHistory = result.processingHistory || {};
          result.processingHistory[f] = defVal;
        }
      }
    });

    // Bank Details
    ;['bankName','routingNumber','accountNumber','accountType'].forEach((f) => {
      const path = `bankDetails.${f}`;
      if (!shouldShowField(path)) {
        const defVal = getFieldDefaultValue?.(path);
        if (defVal !== undefined) {
          result.bankDetails = result.bankDetails || {};
          result.bankDetails[f] = defVal;
        }
      }
    });

    if (!result.pricing) {
      result.pricing = {};
    }
    result.pricing.online = {
      processingFee: result.pricing.online?.processingFee ?? 3.5,
      transactionFee: result.pricing.online?.transactionFee ?? 30,
    };
    result.pricing.offline = {
      processingFee: result.pricing.offline?.processingFee ?? 3.5,
      transactionFee: result.pricing.offline?.transactionFee ?? 30,
    };
    if (result.pricing.chargebackFee === undefined || result.pricing.chargebackFee === null) {
      result.pricing.chargebackFee = 35;
    }
    if (result.pricing.monthlyMinimum === undefined || result.pricing.monthlyMinimum === null) {
      result.pricing.monthlyMinimum = 0;
    }

    return result;
  };
  
  // Fetch banking partners (show all active partners)
  const { data: bankingPartners = [] } = useQuery<BankingPartner[]>({
    queryKey: ['active-banking-partners'],
    queryFn: async () => {
      try {
        const partners = await bankingPartnerService.getAllBankingPartners();
        return partners
          .filter((p: any) => p.status === 'active')
          .map((p: any) => ({ id: p.id, name: p.name, color: p.color || '#000000' }));
      } catch (error) {
        console.error('Error fetching banking partners:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  // Helper function to normalize data structure
  const normalizeApplicationData = (data: any) => {
    if (!data) return {
      personalInfo: {},
      corporationInfo: {},
      processingHistory: {},
      beneficialOwners: [],
      bankDetails: {},
      pricing: {
        online: { processingFee: 3.5, transactionFee: 30 },
        offline: { processingFee: 3.5, transactionFee: 20 }
      },
      pricingTemplateId: undefined,
      selectedBankingPartnerId: undefined,
      other: {},
      payArcMerchantCode: undefined,
      payArcSubmittedAt: undefined,
      integrationLogs: [],
    };
    
    // Handle both flattened and nested structures
    const corporationInfo = data.corporationInfo || {};
    
    // Ensure we have the corporationType synced with businessType
    if (!corporationInfo.businessType && corporationInfo.corporationType) {
      corporationInfo.businessType = corporationInfo.corporationType;
    } else if (corporationInfo.businessType && !corporationInfo.corporationType) {
      corporationInfo.corporationType = corporationInfo.businessType;
    }
    
    // Set default values if missing
    corporationInfo.businessName = corporationInfo.businessName || data.businessName;
    corporationInfo.businessEmail = corporationInfo.customerServiceEmail || data.businessEmail;
    corporationInfo.businessPhone = corporationInfo.businessPhone || data.businessPhone;
    corporationInfo.dba = corporationInfo.dba || data.dba;
    corporationInfo.taxId = corporationInfo.ein || data.ein;
    corporationInfo.businessDescription = corporationInfo.businessDescription || data.businessDescription;
    corporationInfo.yearEstablished = corporationInfo.businessStartDate || data.businessStartDate;
    corporationInfo.website = corporationInfo.website || data.website;
    corporationInfo.customerServiceEmail = corporationInfo.customerServiceEmail || data.customerServiceEmail;
    corporationInfo.customerServicePhone = corporationInfo.customerServicePhone || data.customerServicePhone;
    corporationInfo.companyAddress = corporationInfo.businessAddress || data.businessAddress;
    corporationInfo.sicCode = corporationInfo.sicCode || data.sicCode;
    corporationInfo.sicDescription = corporationInfo.sicDescription || data.sicDescription;
    
    const bankDetails = {
      ...(data.bankDetails || {}),
    } as Record<string, any>;
    if (!bankDetails.bankName) bankDetails.bankName = data.bankName;
    if (!bankDetails.routingNumber) bankDetails.routingNumber = data.routingNumber;
    if (!bankDetails.accountNumber) bankDetails.accountNumber = data.accountNumber;
    if (!bankDetails.accountType) {
      bankDetails.accountType = data.bankDetails?.accountType || data.accountType || undefined;
    }
    
    const personalInfo = data.personalInfo || {};

    const pricing = {
      online: {
        processingFee: data.pricing?.online?.processingFee ?? 3.5,
        transactionFee: data.pricing?.online?.transactionFee ?? 30,
      },
      offline: {
        processingFee: data.pricing?.offline?.processingFee ?? 3.5,
        transactionFee: data.pricing?.offline?.transactionFee ?? 30,
      },
      chargebackFee: data.pricing?.chargebackFee ?? 35,
      monthlyMinimum: data.pricing?.monthlyMinimum ?? 0,
    };
    const other = data.other || { merchantCategory: 'retail' };

    const normalizeLogTimestamp = (value: any): string => {
      if (!value) return new Date().toISOString();
      if (typeof value === 'string') return value;
      if (value instanceof Date) return value.toISOString();
      if (typeof value === 'number') return new Date(value).toISOString();
      if (value?.toDate) {
        try {
          return value.toDate().toISOString();
        } catch {
          return new Date().toISOString();
        }
      }
      return new Date().toISOString();
    };

    const processingHistory = {
      ...(data.processingHistory || {}),
    } as Record<string, any>;
    if (processingHistory.salesDeliveryDays === undefined) {
      const legacySalesDays = (data.processingHistory as any)?.salesDeliveryDays ?? (data as any)?.salesDeliveryDays;
      if (legacySalesDays !== undefined) {
        const numeric = Number(legacySalesDays);
        processingHistory.salesDeliveryDays = Number.isFinite(numeric) ? numeric : 0;
      }
    }

    const integrationLogs = Array.isArray(data.integrationLogs)
      ? data.integrationLogs.map((log: any) => ({
          timestamp: normalizeLogTimestamp(log?.timestamp),
          source: log?.source || 'unknown',
          status: log?.status === 'success' ? 'success' : 'error',
          message: log?.message || 'Unknown event',
          actor: log?.actor,
          details: log?.details,
        }))
      : [];
    
    return {
      personalInfo,
      applicationType: data.applicationType,
      corporationInfo,
      processingHistory,
      beneficialOwners: data.beneficialOwners || [],
      bankDetails,
      pricing,
      pricingTemplateId: data.pricingTemplateId,
      selectedBankingPartnerId: data.selectedBankingPartnerId,
      other,
      payArcMerchantCode: data.payArcMerchantCode,
      payArcSubmittedAt: data.payArcSubmittedAt,
      integrationLogs,
    };
  };

  const appendIntegrationLog = async (
    entry: Omit<IntegrationLogEntry, 'timestamp'> & { timestamp?: string }
  ) => {
    const timestamp = entry.timestamp ?? new Date().toISOString();
    const logEntry: IntegrationLogEntry = {
      ...entry,
      timestamp,
    };

    setFormData((prev) => ({
      ...prev,
      integrationLogs: [...(prev.integrationLogs || []), logEntry],
    }));

    if (!applicationId) return;

    try {
      await updateDoc(doc(db, 'merchantApplications', applicationId), {
        'data.integrationLogs': arrayUnion(logEntry),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to record integration log:', error);
    }
  };

  const deleteIntegrationLog = async (log: IntegrationLogEntry) => {
    const previousLogs = formData.integrationLogs || [];
    const filteredLogs = previousLogs.filter((entry) =>
      entry.timestamp !== log.timestamp ||
      entry.source !== log.source ||
      entry.message !== log.message
    );

    setFormData((prev) => ({
      ...prev,
      integrationLogs: filteredLogs,
    }));

    if (!applicationId) return;

    try {
      await updateDoc(doc(db, 'merchantApplications', applicationId), {
        'data.integrationLogs': filteredLogs,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to delete integration log:', error);
      toast({
        title: 'Unable to delete log',
        description: 'We could not remove that entry. Please try again.',
        variant: 'destructive',
      });

      // Revert state on failure
      setFormData((prev) => ({
        ...prev,
        integrationLogs: previousLogs,
      }));
    }
  };

  // Form data state
  const [formData, setFormData] = useState<{
    personalInfo: Partial<PersonalInfo>;
    corporationInfo: Partial<CorporationInfo>;
    processingHistory: Partial<ProcessingHistory>;
    beneficialOwners: BeneficialOwner[];
    bankDetails: Partial<{
      bankName?: string;
      routingNumber?: string;
      accountNumber?: string;
      accountType?: string;
    }>;
    pricing: Pricing;
    pricingTemplateId?: string;
    selectedBankingPartnerId?: string;
    other?: { merchantCategory?: string };
    payArcMerchantCode?: string;
    payArcSubmittedAt?: Date | { toDate: () => Date };
    integrationLogs: IntegrationLogEntry[];
  }>(normalizeApplicationData(application?.data));

  useEffect(() => {
    if (application) {
      setFormData(normalizeApplicationData(application.data));
    }
  }, [application]);

  const calculateCompletion = () => {
    // If merchant is activated (live), progress is 100%
    if (application?.pipelineStatus === 'live' || application?.status === 'approved') {
      return {
        total: 100,
        completed: 100,
        percentage: 100,
        missing: [],
      };
    }

    const allFields = [
      'corporationInfo.businessName',
      'corporationInfo.dba',
      'corporationInfo.ein',
      'corporationInfo.website',
      'corporationInfo.businessType',
      'corporationInfo.businessAddress.street',
      'corporationInfo.businessAddress.city',
      'corporationInfo.businessAddress.state',
      'corporationInfo.businessAddress.zipCode',
      'processingHistory.monthlyVolume',
      'processingHistory.averageTicket',
      'processingHistory.highTicket',
      'processingHistory.cardPresentPercentage',
      'processingHistory.ecommercePercentage',
      'bankDetails.bankName',
      'bankDetails.routingNumber',
      'bankDetails.accountNumber',
    ];

    // Only count fields that are visible per settings
    const requiredFields = allFields.filter((f) => shouldShowField(f));

    const completed = requiredFields.filter(field => {
      const fieldParts = field.split('.');
      let value: any = formData;
      for (const part of fieldParts) {
        value = value?.[part];
      }
      return value !== null && value !== undefined && value !== '';
    });

    // Missing fields - exclude EIN for sole proprietors
    const isIndividual = (formData as any).applicationType === 'individual' || (application?.data as any)?.applicationType === 'individual';
    const missing = requiredFields.filter(field => {
      // Skip EIN for sole proprietors
      if (field === 'corporationInfo.ein' && isIndividual) {
        return false;
      }

      const fieldParts = field.split('.');
      let value: any = formData;
      for (const part of fieldParts) {
        value = value?.[part];
      }
      return value === null || value === undefined || value === '';
    });

    return {
      total: requiredFields.length,
      completed: completed.length,
      percentage: requiredFields.length > 0 ? Math.round((completed.length / requiredFields.length) * 100) : 0,
      missing,
    };
  };

  const completion = calculateCompletion();

  // Fetch pricing templates for selected banking partner
  const { data: pricingTemplates = [] } = useQuery({
    queryKey: ['pricing-templates', formData.selectedBankingPartnerId],
    queryFn: async () => {
      if (!formData.selectedBankingPartnerId) return [];
      try {
        const templates = await bankingPartnerService.getAllPricingTemplates(formData.selectedBankingPartnerId);
        return templates;
      } catch (error) {
        console.error('Error fetching pricing templates:', error);
        return [];
      }
    },
    enabled: !!formData.selectedBankingPartnerId,
    staleTime: 5 * 60 * 1000,
  });

  // Update field mutation
  const updateFieldMutation = useMutation({
    mutationFn: async ({ field, value }: { field: string; value: any }) => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }
      
      
      // Helper function to remove undefined values recursively
      const removeUndefined = (obj: any): any => {
        if (obj === null || typeof obj !== 'object') {
          return obj;
        }
        
        if (Array.isArray(obj)) {
          return obj.map(removeUndefined);
        }
        
        const cleaned: any = {};
        for (const [key, val] of Object.entries(obj)) {
          if (val !== undefined) {
            cleaned[key] = removeUndefined(val);
          }
        }
        return cleaned;
      };
      
      let cleanedValue = removeUndefined(value);
      
      // Firestore does not accept undefined values
      if (cleanedValue === undefined) {
        cleanedValue = null;
      }
      
      const docRef = doc(db, 'merchantApplications', applicationId);
      const updateData = {
        [`data.${field}`]: cleanedValue,
        updatedAt: serverTimestamp(),
      };
      
      
      await updateDoc(docRef, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchantApplication', applicationId] });
      // Silent success - no toast
    },
    onError: (error) => {
      console.error('Field update error:', error);
      toast({
        title: 'Error',
        description: `Failed to update field: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Delete application mutation
  const deleteApplicationMutation = useMutation({
    mutationFn: async () => {
      await MerchantApplicationService.deleteApplication(applicationId!);
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Application and company reference deleted successfully',
      });
      navigate('/app/merchant-applications');
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete application',
        variant: 'destructive',
      });
    },
  });

  const toggleEdit = (field: string): void => {
    setEditMode((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleAddBank = async () => {
    if (!selectedBankId || !applicationId) return;

    // Check if bank is already assigned
    const currentBanks = application?.assignedBanks || [];
    if (currentBanks.includes(selectedBankId)) {
      toast({
        title: 'Bank already assigned',
        description: 'This bank is already assigned to the merchant',
        variant: 'destructive',
      });
      return;
    }

    try {
      await MerchantApplicationService.addBankToApplication(applicationId, selectedBankId);

      queryClient.invalidateQueries({
        queryKey: ['merchantApplication', applicationId]
      });

      toast({
        title: 'Bank added successfully',
        description: 'The bank has been assigned to the merchant'
      });

      setSelectedBankId(undefined);
    } catch (error) {
      console.error('Error adding bank:', error);
      toast({
        title: 'Error adding bank',
        description: 'There was an error assigning the bank',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveBank = async (bankId: string) => {
    if (!applicationId) return;

    try {
      await MerchantApplicationService.removeBankFromApplication(applicationId, bankId);

      queryClient.invalidateQueries({
        queryKey: ['merchantApplication', applicationId]
      });

      toast({
        title: 'Bank removed successfully',
        description: 'The bank has been removed from the merchant'
      });
    } catch (error) {
      console.error('Error removing bank:', error);
      toast({
        title: 'Error removing bank',
        description: 'There was an error removing the bank',
        variant: 'destructive',
      });
    }
  };

  const handleCopyPayArcCode = async () => {
    if (!formData.payArcMerchantCode) return;

    try {
      await navigator.clipboard.writeText(formData.payArcMerchantCode);
      toast({
        title: 'Code copied',
        description: 'PayArc merchant code copied to clipboard.'
      });
    } catch (error) {
      console.error('Failed to copy PayArc code:', error);
      toast({
        title: 'Copy failed',
        description: 'Unable to copy the PayArc merchant code. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    const fieldParts = field.split('.');
    
    // Strip US- prefix from state fields when saving
    let processedValue = value;
    if ((field.includes('state') || field.includes('State')) && value.startsWith('US-')) {
      processedValue = value.replace('US-', '');
    }
    
    if (fieldParts[0] === 'personalInfo') {
      setFormData((prev) => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          [fieldParts[1]]: processedValue,
        },
      }));
    } else if (fieldParts[0] === 'corporationInfo') {
      if (fieldParts.length === 3 && fieldParts[1] === 'businessAddress') {
        setFormData((prev) => ({
          ...prev,
          corporationInfo: {
            ...prev.corporationInfo,
            businessAddress: {
              ...(prev.corporationInfo?.businessAddress as any || {}),
              [fieldParts[2]]: processedValue,
            },
          },
        }));
      } else {
        // Handle businessType/corporationType synchronization
        if (fieldParts[1] === 'businessType') {
          setFormData((prev) => ({
            ...prev,
            corporationInfo: {
              ...prev.corporationInfo,
              businessType: processedValue,
              corporationType: processedValue, // Save to both fields
            },
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            corporationInfo: {
              ...prev.corporationInfo,
              [fieldParts[1]]: processedValue,
            },
          }));
        }
      }
    } else if (fieldParts[0] === 'processingHistory') {
      // Convert numeric fields to numbers for processingHistory
      const numericFields = [
        'monthlyVolume', 'averageTicket', 'highTicket',
        'cardPresentPercentage', 'ecommercePercentage',
        'retailPercentage', 'onlinePercentage',
        'b2bPercentage', 'b2cPercentage'
      ];
      
      const fieldName = fieldParts[1];
      const finalValue = numericFields.includes(fieldName) 
        ? (parseFloat(processedValue) || 0)
        : processedValue;
      
      setFormData((prev) => ({
        ...prev,
        processingHistory: {
          ...prev.processingHistory,
          [fieldName]: finalValue,
        },
      }));
    } else if (fieldParts[0] === 'bankDetails') {
      setFormData((prev) => ({
        ...prev,
        bankDetails: {
          ...prev.bankDetails,
          [fieldParts[1]]: processedValue,
        },
      }));
    } else if (fieldParts[0] === 'pricing') {
      // Handle pricing fields like pricing.online.processingFee or pricing.chargebackFee
      if (fieldParts.length === 3) {
        const profile = fieldParts[1] as 'online' | 'offline';
        const field = fieldParts[2] as 'processingFee' | 'transactionFee';
        setFormData((prev) => ({
          ...prev,
          pricing: {
            online: prev.pricing?.online || { processingFee: 3.5, transactionFee: 30 },
            offline: prev.pricing?.offline || { processingFee: 3.5, transactionFee: 30 },
            ...(prev.pricing || {}),
            [profile]: {
              ...(prev.pricing?.[profile] || {}),
              [field]: parseFloat(value) || 0,
            },
          },
        }));
      } else if (fieldParts.length === 2) {
        // Handle direct pricing fields like pricing.chargebackFee, pricing.monthlyMinimum
        const field = fieldParts[1] as 'chargebackFee' | 'monthlyMinimum';
        setFormData((prev) => ({
          ...prev,
          pricing: {
            online: prev.pricing?.online || { processingFee: 3.5, transactionFee: 30 },
            offline: prev.pricing?.offline || { processingFee: 3.5, transactionFee: 30 },
            ...(prev.pricing || {}),
            [field]: parseFloat(value) || 0,
          },
        }));
      }
    } else if (fieldParts[0] === 'other') {
      // Handle Other section fields
      setFormData((prev) => ({
        ...prev,
        other: {
          ...(prev.other || {}),
          [fieldParts[1]]: value,
        },
      }));
    }
  };

  const handleBlur = async (field: string) => {
    if (editMode[field]) {
      try {
        // Get the granular value from formData
        const fieldParts = field.split('.');
        let value: any = formData;
        
        // Initial section lookup
        if (fieldParts[0] === 'personalInfo') value = formData.personalInfo;
        else if (fieldParts[0] === 'corporationInfo') value = formData.corporationInfo;
        else if (fieldParts[0] === 'processingHistory') value = formData.processingHistory;
        else if (fieldParts[0] === 'bankDetails') value = formData.bankDetails;
        else if (fieldParts[0] === 'pricing') value = formData.pricing;
        else if (fieldParts[0] === 'other') value = formData.other;
        
        // Traverse for nested fields
        for (let i = 1; i < fieldParts.length; i++) {
          if (value === undefined || value === null) break;
          value = value[fieldParts[i]];
        }
        
        // Granular update using dot notation
        await updateFieldMutation.mutateAsync({ field, value });
        toggleEdit(field);
      } catch (error) {
        // Error handled by mutation
        return;
      }
    }
  };

  const handleEditableKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    if (e.key === 'Enter') {
      // Trigger onBlur save path
      (e.currentTarget as HTMLElement).blur();
    } else if (e.key === 'Escape') {
      // Exit edit mode without saving
      toggleEdit(field);
    }
  };

  // Handler to retrieve bearer key from PayArc
  const handleRetrieveBearerKey = async () => {
    if (!applicationId || !application) {
      toast({
        title: 'Error',
        description: 'Application data is required',
        variant: 'destructive',
      });
      return;
    }

    const mid = (application as any)?.mid;
    const accountId = (application as any)?.accountId;

    if (!accountId) {
      toast({
        title: 'Account ID Required',
        description: 'The merchant must have a PayArc Account ID to retrieve the bearer key. This is provided when PayArc sends the "Merchant Activated" webhook. The merchant may not be fully activated yet.',
        variant: 'destructive',
      });
      return;
    }

    // Find the PayArc banking partner ID
    const payArcBankId = application.assignedBanks?.find(bankId => {
      const bank = bankingPartners.find(p => p.id === bankId);
      return bank?.name.toLowerCase().includes('payarc');
    });

    if (!payArcBankId) {
      toast({
        title: 'PayArc Not Assigned',
        description: 'Please assign PayArc as a banking partner first',
        variant: 'destructive',
      });
      return;
    }

    setIsRetrievingBearerKey(true);

    try {
      const response = await fetch('/api/payarc/retrieve-bearer-key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken(auth.currentUser!)}`,
        },
        body: JSON.stringify({
          applicationId,
          mid,
          accountId,
          bankingPartnerId: payArcBankId,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || result.error || 'Failed to retrieve bearer key');
      }

      // Invalidate query to refresh the application data with new bearer key
      queryClient.invalidateQueries({ queryKey: ['merchantApplication', applicationId] });

      toast({
        title: 'Bearer Key Retrieved',
        description: 'The merchant bearer key has been successfully retrieved and saved',
      });
    } catch (error) {
      console.error('[Bearer Key] Error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to retrieve bearer key',
        variant: 'destructive',
      });
    } finally {
      setIsRetrievingBearerKey(false);
    }
  };

  const handleSendToPayArc = async () => {
    if (!applicationId) {
      toast({
        title: 'Error',
        description: 'Application ID is required',
        variant: 'destructive',
      });
      return;
    }

    setIsPayArcSending(true);
    try {
      // Fetch fresh data from Firestore to ensure CRM edits are included
      // This bypasses React Query cache which may be stale
      console.log('🔍 [PayArc] Fetching fresh application data from Firestore...');
      const freshApplication = await MerchantApplicationService.getApplication(applicationId);

      if (!freshApplication?.data) {
        toast({
          title: 'Error',
          description: 'No application data available',
          variant: 'destructive',
        });
        setIsPayArcSending(false);
        return;
      }

      // Debug logging to diagnose bankingPartnerId issue
      console.log('[PayArc Debug] assignedBanks:', freshApplication.assignedBanks);
      console.log('[PayArc Debug] bankingPartners available:', bankingPartners.length, bankingPartners.map(p => ({id: p.id, name: p.name})));

      // Check if bankingPartners is loaded
      if (bankingPartners.length === 0) {
        toast({
          title: 'Loading...',
          description: 'Banking partners still loading. Please try again.',
          variant: 'destructive',
        });
        setIsPayArcSending(false);
        return;
      }

      // Check if PayArc is assigned as a banking partner and get the banking partner ID
      const payArcBankId = freshApplication.assignedBanks?.find(bankId => {
        const bank = bankingPartners.find(p => p.id === bankId);
        console.log('[PayArc Debug] Checking bankId:', bankId, '-> found bank:', bank?.name || 'NOT FOUND');
        return bank?.name.toLowerCase().includes('payarc');
      });

      if (!payArcBankId) {
        const assignedBankDetails = freshApplication.assignedBanks?.map(bankId => {
          const bank = bankingPartners.find(p => p.id === bankId);
          return { bankId, bankName: bank?.name || 'NOT FOUND IN PARTNERS LIST' };
        });
        console.error('[PayArc] No PayArc bank found. Assigned banks:', assignedBankDetails);
        toast({
          title: 'PayArc Not Assigned',
          description: `Please assign PayArc as a banking partner first. Currently assigned: ${assignedBankDetails?.map(b => b.bankName).join(', ') || 'None'}`,
          variant: 'destructive',
        });
        setIsPayArcSending(false);
        return;
      }

      // Merge local formData over fresh application data to capture unsaved edits
      const dataToSendSource = {
        ...freshApplication.data,
        ...formData,
      };

      console.log('🔑 [PayArc] Using banking partner ID:', payArcBankId);
      console.log('📋 [PayArc] Fresh beneficial owners:', dataToSendSource.beneficialOwners?.map((o: any) => ({
        name: `${o.firstName} ${o.lastName}`,
        city: o.homeAddress?.city || o.city,
      })));
      console.log('📋 [PayArc] Fresh business address:', {
        street: dataToSendSource.corporationInfo?.businessAddress?.street,
        city: dataToSendSource.corporationInfo?.businessAddress?.city,
        state: dataToSendSource.corporationInfo?.businessAddress?.state,
      });

      const toSend = hydrateWithDefaults(dataToSendSource);
      console.log('🚀 [PayArc] Sending application to PayArc with fresh data and banking partner ID:', payArcBankId);
      const response = await payarcService.createLead(toSend, payArcBankId);

      if (response.success) {
        setPayArcResponse(response);

        const successMessage = response.MerchantCode
          ? `Application sent to PayArc. Merchant Code: ${response.MerchantCode}`
          : 'Application sent to PayArc successfully.';

        toast({
          title: 'Success!',
          description: successMessage,
        });

        if (response.MerchantCode) {
          setFormData((prev) => ({
            ...prev,
            payArcMerchantCode: response.MerchantCode,
            payArcSubmittedAt: new Date(),
          }));
        }

        // Update application status or add note about PayArc submission
        if (applicationId) {
          try {
            const docRef = doc(db, 'merchantApplications', applicationId);
            await updateDoc(docRef, {
              ...(response.MerchantCode
                ? {
                    'data.payArcMerchantCode': response.MerchantCode,
                    'data.payArcSubmittedAt': serverTimestamp(),
                  }
                : {}),
              updatedAt: serverTimestamp(),
            });
          } catch (updateError) {
            console.warn('Could not update PayArc merchant code:', updateError);
          }
        }

        await appendIntegrationLog({
          source: 'payarc-lead',
          status: 'success',
          message: successMessage,
          actor: 'crm-admin',
          details: response?.data ? JSON.stringify(response.data, null, 2) : undefined,
        });
      } else {
        const errorMessage = response.message || 'Failed to send application to PayArc';
        console.error('PayArc API Error:', response);
        toast({
          title: 'PayArc Error',
          description: errorMessage,
          variant: 'destructive',
        });
        await appendIntegrationLog({
          source: 'payarc-lead',
          status: 'error',
          message: errorMessage,
          actor: 'crm-admin',
          details: JSON.stringify(response.errors ?? response, null, 2),
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send application to PayArc';
      console.error('Error sending to PayArc:', error);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      await appendIntegrationLog({
        source: 'payarc-lead',
        status: 'error',
        message,
        actor: 'crm-admin',
        details: error instanceof Error
          ? error.stack || error.message
          : JSON.stringify(error, null, 2),
      });
    } finally {
      setIsPayArcSending(false);
    }
  };

  const handleExaminePayArc = async () => {
    if (!application?.data) {
      toast({
        title: 'Error',
        description: 'No application data available',
        variant: 'destructive',
      });
      return;
    }

    // Merge local formData over application data to capture unsaved edits
    const dataToPreview = {
      ...application.data,
      ...formData,
    };

    // Generate the payload and validation errors
    const { payload, errors } = await payarcService.generatePayload(hydrateWithDefaults(dataToPreview));
    setPayArcPayloadPreview({ payload, errors });
    setEditedPayloadText(JSON.stringify(payload, null, 2));
    setIsExamineDialogOpen(true);
  };

  const handleExamineSubmission = async () => {
    if (!formData.payArcMerchantCode) {
      toast({
        title: 'Error',
        description: 'No merchant code available. Send the application to PayArc first.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Build the base submission payload
      const submissionPayload: Record<string, unknown> = {
        merchant_code: formData.payArcMerchantCode,
        MerchantCode: formData.payArcMerchantCode,
      };

      // Fetch lead-status from PayArc to get apply_app_id and lead_code
      try {
        const leadStatusResponse = await payarcService.getLeadStatus(formData.payArcMerchantCode);

        if (leadStatusResponse.success && leadStatusResponse.data) {
          const rawStatus = leadStatusResponse.data as Record<string, any>;

          // Helper function to find nested values (same logic as in payarcService)
          const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '');
          const findNestedValue = (root: unknown, candidateKeys: string[]): unknown => {
            const targetKeys = candidateKeys.map(normalizeKey);
            const stack: unknown[] = [root];
            const visited = new Set<unknown>();

            while (stack.length > 0) {
              const current = stack.pop();
              if (!current || typeof current !== 'object') continue;
              if (visited.has(current)) continue;
              visited.add(current);

              if (Array.isArray(current)) {
                for (const item of current) {
                  if (item && typeof item === 'object') {
                    stack.push(item);
                  }
                }
                continue;
              }

              for (const [key, value] of Object.entries(current as Record<string, unknown>)) {
                const normalized = normalizeKey(key);
                if (targetKeys.includes(normalized) && (typeof value === 'string' || typeof value === 'number')) {
                  return value;
                }

                if (value && typeof value === 'object') {
                  stack.push(value);
                }
              }
            }
            return undefined;
          };

          // Extract apply_app_id
          const applyAppId =
            findNestedValue(rawStatus, [
              'apply_app_id', 'applyappid', 'applyid', 'applicationid', 'applyapplicationid',
            ]) ||
            rawStatus.Apply_App_Id ||
            rawStatus.apply_app_id ||
            rawStatus.ApplyAppId ||
            rawStatus.applyAppId ||
            rawStatus?.Lead?.Apply_App_Id ||
            rawStatus?.Lead?.apply_app_id ||
            rawStatus.LeadCode ||
            rawStatus.leadCode ||
            rawStatus?.Lead?.LeadCode;

          if (applyAppId !== undefined && applyAppId !== null && applyAppId !== '') {
            const normalized = String(applyAppId);
            submissionPayload.apply_app_id = normalized;
            submissionPayload.applyAppId = normalized;
          }

          // Extract lead_code
          const leadCode =
            findNestedValue(rawStatus, [
              'leadcode', 'lead_id', 'leadid', 'leadidentifier',
            ]) ||
            rawStatus.LeadCode ||
            rawStatus.leadCode ||
            rawStatus?.Lead?.LeadCode ||
            rawStatus?.lead?.leadCode;

          if (leadCode !== undefined && leadCode !== null && leadCode !== '') {
            const normalized = String(leadCode);
            submissionPayload.lead_code = normalized;
            submissionPayload.LeadCode = normalized;
          }
        }
      } catch (statusError) {
        console.warn('Could not fetch lead-status for preview:', statusError);
        // Continue with just merchant code if lead-status fails
      }

      setSubmissionPayloadPreview({ payload: submissionPayload, errors: [] });
      setEditedSubmissionPayloadText(JSON.stringify(submissionPayload, null, 2));
      setIsExamineSubmissionDialogOpen(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to prepare submission payload',
        variant: 'destructive',
      });
    }
  };

  const handleSubmitPayArcApplication = async () => {
    if (!formData.payArcMerchantCode) {
      toast({
        title: 'Missing Merchant Code',
        description: 'Send the application to PayArc first to generate a merchant code.',
        variant: 'destructive',
      });
      return;
    }

    if (!applicationId) {
      toast({
        title: 'Error',
        description: 'Cannot determine application ID.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmittingPayArc(true);
    try {
      // Get the first assigned PayArc banking partner
      const payArcBankId = application?.assignedBanks?.find((bankId) => {
        // This will be checked in the API route, but we pass it here
        return bankId; // We'll filter for PayArc in the API
      });

      const response = await payarcService.submitApplicationForUnderwriting(
        formData.payArcMerchantCode,
        payArcBankId
      );

      if (!response.success) {
        const message = response.message || 'Failed to submit PayArc application';
        toast({
          title: 'Submission failed',
          description: message,
          variant: 'destructive',
        });
        await appendIntegrationLog({
          source: 'payarc-submit',
          status: 'error',
          message,
          actor: 'crm-admin',
          details: JSON.stringify(response.errors ?? response, null, 2),
        });
        const error = new Error(message);
        (error as any).details = response.errors ?? response;
        throw error;
      }

      const submissionTimestamp = new Date();

      await updateDoc(doc(db, 'merchantApplications', applicationId), {
        status: 'submitted',
        pipelineStatus: 'underwriting',
        'data.payArcSubmittedAt': serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (application?.companyId) {
        await updateDoc(doc(db, 'companies', application.companyId), {
          'merchantApplication.status': 'submitted',
          'merchantApplication.submittedAt': serverTimestamp(),
          'merchantApplication.applicationId': applicationId,
          'merchantApplication.payArcMerchantCode': formData.payArcMerchantCode,
        });
      }

      await appendIntegrationLog({
        source: 'payarc-submit',
        status: 'success',
        message: 'Submitted PayArc application to underwriting',
        actor: 'crm-admin',
        details: response?.data ? JSON.stringify(response.data, null, 2) : undefined,
      });

      setFormData((prev) => ({
        ...prev,
        payArcSubmittedAt: submissionTimestamp,
      }));

      toast({
        title: 'Application submitted',
        description: 'PayArc underwriting submission completed successfully.',
      });

      queryClient.invalidateQueries({ queryKey: ['merchantApplication', applicationId] });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to submit PayArc application';
      console.error('Error submitting PayArc application:', error);
      toast({
        title: 'Submission failed',
        description: message,
        variant: 'destructive',
      });
      await appendIntegrationLog({
        source: 'payarc-submit',
        status: 'error',
        message,
        actor: 'crm-admin',
        details: error instanceof Error
          ? (typeof (error as any).details === 'string'
              ? (error as any).details
              : (error as any).details
                ? JSON.stringify((error as any).details, null, 2)
                : error.stack || error.message)
          : JSON.stringify(error, null, 2),
      });
    } finally {
      setIsSubmittingPayArc(false);
    }
  };

  const calculateProgress = (status: PipelineStatus | undefined | null): number => {
    // Safety check for undefined or null status
    if (!status) {
      console.warn('calculateProgress: status is undefined or null, defaulting to 0%');
      return 0;
    }

    // Include all valid pipeline statuses in the correct order
    const stages: PipelineStatus[] = ['lead', 'phone', 'offer', 'underwriting', 'documents', 'approved', 'onboarded', 'declined'];

    const currentIndex = stages.indexOf(status);

    // If status is not found in stages, log warning and return 0
    if (currentIndex === -1) {
      console.warn(`calculateProgress: Unknown status "${status}", defaulting to 0%`);
      return 0;
    }

    // Special handling for declined status - show as 0% progress
    if (status === 'declined') {
      return 0;
    }

    // For onboarded, show as 100% complete
    if (status === 'onboarded') {
      return 100;
    }

    // Calculate progress for other statuses
    return ((currentIndex + 1) / (stages.length - 2)) * 100; // Subtract 2 to exclude onboarded and declined from progress calculation
  };

  const getOwnershipColor = (percentage: number) => {
    if (percentage === 100) return 'bg-green-100 text-green-800 border-green-300';
    if (percentage > 100) return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  };

  const sortedIntegrationLogs = useMemo(() => {
    const logs = formData.integrationLogs || [];
    return [...logs].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [formData.integrationLogs]);

  const renderEditableField = (
    field: string,
    placeholder: string,
    icon: React.ReactNode | null,
    type: string = 'text'
  ) => {
    const fieldParts = field.split('.');
    let value: any = '';
    
    // Handle nested fields with multiple levels
    if (fieldParts[0] === 'personalInfo' && formData.personalInfo) {
      if (fieldParts.length === 2) {
        value = (formData.personalInfo as any)[fieldParts[1]] || '';
      }
    } else if (fieldParts[0] === 'corporationInfo' && formData.corporationInfo) {
      if (fieldParts.length === 2) {
        value = (formData.corporationInfo as any)[fieldParts[1]] || '';
      } else if (fieldParts.length === 3 && fieldParts[1] === 'businessAddress') {
        const address = (formData.corporationInfo as any).businessAddress;
        value = address ? address[fieldParts[2]] || '' : '';
      }
    } else if (fieldParts[0] === 'processingHistory' && formData.processingHistory) {
      value = (formData.processingHistory as any)[fieldParts[1]] || '';
    } else if (fieldParts[0] === 'bankDetails' && formData.bankDetails) {
      value = (formData.bankDetails as any)[fieldParts[1]] || '';
    } else if (fieldParts[0] === 'pricing') {
      if (fieldParts.length === 3) {
        const profile = fieldParts[1] as 'online' | 'offline';
        const field = fieldParts[2] as 'processingFee' | 'transactionFee';
        const pricingProfile = formData.pricing?.[profile];
        value = pricingProfile ? (pricingProfile as any)[field] ?? '' : '';
      } else if (fieldParts.length === 2) {
        // Handle direct pricing fields like pricing.chargebackFee, pricing.monthlyMinimum
        const field = fieldParts[1] as 'chargebackFee' | 'monthlyMinimum';
        value = formData.pricing ? (formData.pricing as any)[field] ?? '' : '';
      }
    }

    // Ensure value is always a string
    if (typeof value === 'object') {
      value = '';
    }

    return (
      <div className="flex items-center gap-3 py-1.5 px-2 hover:bg-gray-50/80 rounded transition-colors group">
        <div className="flex-1 min-w-0">
          <label className="text-xs text-gray-900 font-medium block mb-1">{placeholder}</label>
          {editMode[field] ? (
            type === 'textarea' ? (
              <Textarea
                value={value}
                onChange={(e) => handleInputChange(field, e.target.value)}
                onBlur={() => handleBlur(field)}
                onKeyDown={(e) => handleEditableKeyDown(e, field)}
                className="min-h-[60px] h-auto resize-none focus-visible:ring-1 focus-visible:ring-primary/50 text-sm"
                placeholder=""
                autoFocus
              />
            ) : (
              <Input
                type={type}
                value={value}
                onChange={(e) => handleInputChange(field, e.target.value)}
                onBlur={() => handleBlur(field)}
                onKeyDown={(e) => handleEditableKeyDown(e, field)}
                className="h-8 focus-visible:ring-1 focus-visible:ring-primary/50 text-sm"
                placeholder=""
                autoFocus
              />
            )
          ) : (
            <div
              className="text-sm font-medium cursor-pointer rounded px-1 py-0.5 transition-colors group-hover:bg-white min-h-[20px] flex items-center"
              onClick={() => toggleEdit(field)}
            >
              <span className="text-gray-600">{value}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const chargeTimingLabel = (value: string | undefined) => {
    if (!value) return '';
    const normalized = value.toLowerCase();
    const map: Record<string, string> = {
      order: 'At order',
      delivery: 'At delivery',
      shipment: 'At shipment',
    };
    return map[normalized] || value;
  };

  const renderInlineField = (
    field: string,
    label: string,
    icon: React.ReactNode | null,
    type: string = 'text',
    options: { value: string; label: string }[] = []
  ) => {
    const accountTypeOptions = [
      { label: 'Checking', value: 'checking' },
      { label: 'Savings', value: 'savings' },
    ];
    const fieldParts = field.split('.');
    let value: any = '';
    if (fieldParts[0] === 'personalInfo' && formData.personalInfo) {
      if (fieldParts.length === 2) {
        value = (formData.personalInfo as any)[fieldParts[1]] || '';
      }
    } else if (fieldParts[0] === 'corporationInfo' && formData.corporationInfo) {
      if (fieldParts.length === 2) {
        value = (formData.corporationInfo as any)[fieldParts[1]] || '';
      } else if (fieldParts.length === 3 && fieldParts[1] === 'businessAddress') {
        const address = (formData.corporationInfo as any).businessAddress;
        value = address ? address[fieldParts[2]] || '' : '';
      }
    } else if (fieldParts[0] === 'processingHistory' && formData.processingHistory) {
      value = (formData.processingHistory as any)[fieldParts[1]] || '';
    } else if (fieldParts[0] === 'bankDetails' && formData.bankDetails) {
      value = (formData.bankDetails as any)[fieldParts[1]] || '';
    } else if (fieldParts[0] === 'pricing') {
      if (fieldParts.length === 3) {
        const profile = fieldParts[1] as 'online' | 'offline';
        const f = fieldParts[2] as 'processingFee' | 'transactionFee';
        const pricingProfile = formData.pricing?.[profile];
        value = pricingProfile ? (pricingProfile as any)[f] ?? '' : '';
      } else if (fieldParts.length === 2) {
        const f = fieldParts[1] as 'chargebackFee' | 'monthlyMinimum';
        value = formData.pricing ? (formData.pricing as any)[f] ?? '' : '';
      }
    }
    if (typeof value === 'object') value = '';

    // Check if this is a monetary field that should show $ prefix
    const isMoneyField = type === 'number' && (
      field.includes('monthlyVolume') || 
      field.includes('averageTicket') || 
      field.includes('highTicket') ||
      field.includes('chargebackFee') ||
      field.includes('monthlyMinimum')
    );

    // Check if this is a state field that should strip US- prefix
    const isStateField = field.includes('state') || field.includes('State');
    let processedValue = value;
    if (isStateField && typeof value === 'string' && value.startsWith('US-')) {
      processedValue = value.replace('US-', '');
    }

    const isEditing = editMode[field];
    const labelClasses = "text-[10px] text-gray-500 uppercase font-semibold tracking-wider mb-0.5 pl-0.5";
    
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
          (e.currentTarget as HTMLElement).blur();
        } else if (e.key === 'Escape') {
          toggleEdit(field);
        }
    };

    // Masking Logic
    const isEin = field.toLowerCase().includes('ein') || field.toLowerCase().includes('taxid');
    const isPhone = field.toLowerCase().includes('phone');
    
    const handleMaskedChange = (val: string) => {
        let finalVal = val;
        if (isEin) {
             const digits = val.replace(/\D/g, '');
             if (digits.length > 2) finalVal = `${digits.slice(0,2)}-${digits.slice(2,9)}`;
             else finalVal = digits;
        } else if (isPhone) {
             finalVal = formatPhoneNumber(val);
        }
        handleInputChange(field, finalVal);
    };

    // Input Styles
    const inputClasses = cn(
        "h-8 text-sm w-full transition-all duration-200 px-2 py-1 rounded-md border",
        isEditing 
            ? "bg-white border-input shadow-sm focus-visible:ring-2 focus-visible:ring-primary/60" 
            : "bg-transparent border-transparent hover:bg-gray-50/80 cursor-text truncate text-gray-900 placeholder:text-gray-400"
    );

    if (type === 'select') {
        let selectOptions: {label: string, value: string}[] = [];
        let placeholder = "Select...";
        
        if (field === 'corporationInfo.businessType') {
            selectOptions = CORPORATION_TYPES;
            placeholder = "Select Type";
        } else if (field === 'bankDetails.accountType') {
            selectOptions = accountTypeOptions;
            placeholder = "Select Account";
        } else if (field === 'processingHistory.whenIsCustomerCharged') {
            selectOptions = [
              { value: "order", label: "At order" },
              { value: "delivery", label: "At delivery" },
              { value: "shipment", label: "At shipment" },
              ...options
            ];
            placeholder = "Select Timing";
        } else if (field === 'processingHistory.advancedDeposits') {
            selectOptions = [
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" }
            ];
            placeholder = "Collect Deposits?";
        } else if (options.length > 0) {
            selectOptions = options;
        }

        const selectedLabel = selectOptions.find(o => o.value === value)?.label || value;

        return (
            <div className="flex flex-col w-full group">
                <div className={labelClasses}>{label}</div>
                <Select 
                    value={value || ''} 
                    onValueChange={(newValue) => {
                      handleInputChange(field, newValue);
                      setTimeout(() => handleBlur(field), 100);
                    }}
                >
                    <SelectTrigger className={cn(inputClasses, "justify-between px-2")}>
                         <SelectValue placeholder={placeholder}>
                            {selectedLabel || <span className="text-gray-400 text-xs italic">Empty</span>}
                         </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                        {selectOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    }

    if (type === 'textarea') {
        // Special handling for business description - open modal instead of inline editing
        if (field === 'corporationInfo.businessDescription') {
            const handleClick = () => {
                const currentValue = value || '';
                setBusinessDescriptionValue(currentValue);
                setIsBusinessDescriptionModalOpen(true);
            };
            
            return (
                <div className="flex flex-col w-full group">
                    <div className={labelClasses}>{label}</div>
                    <div
                        onClick={handleClick}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleClick();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        className={cn(
                            "min-h-[60px] max-h-[60px] text-sm w-full p-2 rounded-md transition-all border cursor-pointer",
                            "bg-transparent border-transparent hover:bg-gray-50/80 hover:border-gray-200",
                            "overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        )}
                    >
                        <div className="text-sm text-gray-900 line-clamp-3 break-words">
                            {value || <span className="text-gray-400 italic">Click to edit business description</span>}
                        </div>
                    </div>
                </div>
            );
        }
        
        return (
            <div className="flex flex-col w-full group">
                <div className={labelClasses}>{label}</div>
                <Textarea
                    value={value}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    onBlur={() => handleBlur(field)}
                    onKeyDown={handleKeyDown}
                    className={cn(
                        "min-h-[50px] resize-none text-sm w-full p-2 rounded-md transition-all border",
                        isEditing ? "bg-white border-input shadow-sm" : "bg-transparent border-transparent hover:bg-gray-50/80"
                    )}
                    placeholder={isEditing ? "" : "-"}
                    readOnly={!isEditing}
                    onClick={() => !isEditing && toggleEdit(field)}
                />
            </div>
        );
    }

    // Check if this is the DBA field for 25-char limit
    const isDbaField = field === 'corporationInfo.dba';
    const dbaCharCount = isDbaField ? (value?.length || 0) : 0;

    return (
      <div className="flex flex-col w-full group relative">
         <div className="flex justify-between items-center">
           <div className={labelClasses}>{label}</div>
         </div>
         <div className="relative">
             <Input
                type={isMoneyField && !isEditing ? "text" : type}
                value={isMoneyField && !isEditing ? `$${value}` : value}
                onChange={(e) => isEin || isPhone ? handleMaskedChange(e.target.value) : handleInputChange(field, e.target.value)}
                onBlur={() => handleBlur(field)}
                onFocus={() => !isEditing && toggleEdit(field)}
                onKeyDown={handleKeyDown}
                className={inputClasses}
                readOnly={!isEditing}
                placeholder={!isEditing && !value ? "-" : ""}
                maxLength={isDbaField ? 25 : undefined}
             />
         </div>
      </div>
    );
  };

  const renderInlineFieldOneLine = (
    field: string,
    label: string,
    icon: React.ReactNode | null,
    type: string = 'text'
  ) => {
    const fieldParts = field.split('.');
    let value: any = '';
    if (fieldParts[0] === 'personalInfo' && formData.personalInfo) {
      if (fieldParts.length === 2) {
        value = (formData.personalInfo as any)[fieldParts[1]] || '';
      }
    } else if (fieldParts[0] === 'corporationInfo' && formData.corporationInfo) {
      if (fieldParts.length === 2) {
        value = (formData.corporationInfo as any)[fieldParts[1]] || '';
      } else if (fieldParts.length === 3 && fieldParts[1] === 'businessAddress') {
        const address = (formData.corporationInfo as any).businessAddress;
        value = address ? address[fieldParts[2]] || '' : '';
      }
    } else if (fieldParts[0] === 'processingHistory' && formData.processingHistory) {
      value = (formData.processingHistory as any)[fieldParts[1]] || '';
    } else if (fieldParts[0] === 'bankDetails' && formData.bankDetails) {
      value = (formData.bankDetails as any)[fieldParts[1]] || '';
    } else if (fieldParts[0] === 'pricing') {
      if (fieldParts.length === 3) {
        const profile = fieldParts[1] as 'online' | 'offline';
        const f = fieldParts[2] as 'processingFee' | 'transactionFee';
        const pricingProfile = formData.pricing?.[profile];
        value = pricingProfile ? (pricingProfile as any)[f] ?? '' : '';
      } else if (fieldParts.length === 2) {
        const f = fieldParts[1] as 'chargebackFee' | 'monthlyMinimum';
        value = formData.pricing ? (formData.pricing as any)[f] ?? '' : '';
      }
    }
    if (typeof value === 'object') value = '';

    const isEditing = editMode[field];
    
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
          (e.currentTarget as HTMLElement).blur();
        } else if (e.key === 'Escape') {
          toggleEdit(field);
        }
    };

    const inputClasses = cn(
        "h-7 text-sm flex-1 transition-all duration-200 px-2 py-0.5 rounded-md border",
        isEditing 
            ? "bg-white border-input shadow-sm focus-visible:ring-2 focus-visible:ring-primary/60" 
            : "bg-transparent border-transparent hover:bg-gray-50/80 cursor-text truncate text-gray-900 placeholder:text-gray-400"
    );

    return (
      <div className="flex items-center gap-2 px-2 py-1 rounded-md transition-all duration-150 group">
        <div className="flex items-center gap-2 flex-1">
          <span className="text-xs text-gray-900 font-medium min-w-[80px]">{label}:</span>
          {type === 'textarea' ? (
              <Textarea
                value={value}
                onChange={(e) => handleInputChange(field, e.target.value)}
                onBlur={() => handleBlur(field)}
                onKeyDown={handleKeyDown}
                className={cn(inputClasses, "min-h-[50px] h-auto resize-none")}
                readOnly={!isEditing}
                onClick={() => !isEditing && toggleEdit(field)}
              />
            ) : (
              <Input
                type={type}
                value={value}
                onChange={(e) => handleInputChange(field, e.target.value)}
                onBlur={() => handleBlur(field)}
                onKeyDown={handleKeyDown}
                className={inputClasses}
                readOnly={!isEditing}
                onClick={() => !isEditing && toggleEdit(field)}
              />
            )
          }
        </div>
      </div>
    );
  };

  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex gap-8">
          <div className="w-[35%] space-y-4">
            <div className="h-[200px] w-full bg-gray-200 animate-pulse rounded" />
            <div className="h-[300px] w-full bg-gray-200 animate-pulse rounded" />
          </div>
          <div className="w-[65%]">
            <div className="h-[500px] w-full bg-gray-200 animate-pulse rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="p-8 text-center text-red-500">
        Error loading application. Please try again.
      </div>
    );
  }

  const statusColor = PIPELINE_COLORS[application.pipelineStatus] || '#6B7280';
  const totalOwnership = formData.beneficialOwners?.reduce(
    (sum: number, owner: BeneficialOwner) => sum + (Number(owner.ownershipPercentage) || 0),
    0
  ) || 0;

  // Business display helpers (visual only)
  const businessName = (application?.data as any)?.corporationInfo?.businessName 
    || (application?.data as any)?.businessName 
    || formData.corporationInfo?.businessName 
    || 'Unnamed Business';
  const businessInitial = (businessName?.trim()?.charAt(0) || 'B').toUpperCase();
  console.log(formData.selectedBankingPartnerId)
  return (
    <div className="p-8 w-full max-w-[100vw] overflow-x-hidden">
      <div className="flex gap-6 flex-col lg:flex-row">
        {/* Enhanced Left Column with Modern Design */}
        <div className="flex flex-col w-full lg:w-[30%] min-w-[320px] lg:min-w-[400px] sticky top-6 h-fit space-y-4 animate-in fade-in">
          {/* Enhanced Progress Summary Card - Hide when merchant is live, processing, or approved */}
          {!(application?.pipelineStatus === 'live' || application?.pipelineStatus === 'processing' || application?.status === 'approved') && (
            <Card className="relative overflow-hidden bg-white shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-indigo-500/5" />
              <CardHeader className="relative space-y-3 pb-3">
                {/* Enhanced Progress Bar with Gradient */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-medium">
                    <span className="text-gray-600">Application Progress</span>
                    <span className="text-blue-600">
                      {completion.percentage >= 100 ? 'Complete!' : 'In Progress'} ({completion.percentage}%)
                    </span>
                  </div>
                  <div className="relative">
                    <Progress
                      value={completion.percentage}
                      className="h-2.5 bg-gray-100"
                    />
                    <div
                      className="absolute top-0 left-0 h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
                      style={{ width: `${completion.percentage}%` }}
                    />
                  </div>
                </div>

                {/* PayArc Integration IDs */}
                {(application.merchantCode || application.mid) && (
                  <div className="pt-3 border-t border-gray-100 space-y-2">
                    {application.merchantCode && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600 font-medium flex items-center gap-1.5">
                          <Hash className="h-3.5 w-3.5" />
                          Merchant Code
                        </span>
                        <div className="flex items-center gap-2">
                          <code className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded font-mono">
                            {application.merchantCode}
                          </code>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => {
                                    navigator.clipboard.writeText(application.merchantCode || '');
                                    toast({ title: 'Copied!', description: 'Merchant Code copied to clipboard' });
                                  }}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Copy Merchant Code</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    )}
                    {application.mid && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600 font-medium flex items-center gap-1.5">
                          <CreditCard className="h-3.5 w-3.5" />
                          MID (Merchant ID)
                        </span>
                        <div className="flex items-center gap-2">
                          <code className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-mono font-semibold">
                            {application.mid}
                          </code>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => {
                                    navigator.clipboard.writeText(application.mid || '');
                                    toast({ title: 'Copied!', description: 'MID copied to clipboard' });
                                  }}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Copy MID</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent className="relative pt-0">
                {/* Completion Checklist */}
                {completion.missing.length > 0 && (
                  <div className="mb-3 p-2.5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                        <Info className="h-4 w-4 text-amber-600" />
                        Missing Information
                      </h3>
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                        {completion.missing.length} items
                      </Badge>
                    </div>
                    <div className="space-y-1.5">
                      {completion.missing.slice(0, 5).map((field) => (
                        <div key={field} className="flex items-center gap-2 text-xs">
                          <XCircle className="h-3.5 w-3.5 text-amber-600" />
                          <span className="text-gray-700">
                            {field.split('.').pop()?.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} required
                          </span>
                        </div>
                      ))}
                      {completion.missing.length > 5 && (
                        <div className="text-xs text-amber-600 font-medium pl-5">
                          +{completion.missing.length - 5} more fields
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Enhanced Information Sections */}
          <Card className="bg-white shadow-lg hover:shadow-xl transition-all duration-300">
            <CardContent className="p-0">
              <Accordion type="single" collapsible className="w-full">
                {/* Enhanced Business Section */}
                <AccordionItem value="business" className="border-b border-gray-100">
                  <AccordionTrigger className="hover:no-underline px-4 py-3 rounded-md border border-transparent hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 transition-all duration-200 focus-visible:outline-none focus-visible:ring-0 data-[state=open]:ring-1 data-[state=open]:ring-blue-200">
                    <div className="flex items-center justify-between w-full pr-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg">
                          <Building2 className="h-3.5 w-3.5 text-white" />
                        </div>
                        <span className="text-gray-900 text-sm font-semibold">Business Information</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          8/10 Complete
                        </Badge>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-2">
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          {shouldShowField('corporationInfo.dba') && renderInlineField(
                            'corporationInfo.dba',
                            'DBA',
                            <Building className="h-4 w-4 text-blue-500" />
                          )}
                          {shouldShowField('corporationInfo.ein') && renderInlineField(
                            'corporationInfo.ein',
                            'Tax ID / EIN',
                            <Hash className="h-4 w-4 text-blue-500" />
                          )}
                        </div>

                        {/* Website with clickable link */}
                        {shouldShowField('corporationInfo.website') && (
                        <div className={cn(
                          "flex items-center gap-3 py-1 rounded transition-colors group",
                          "hover:bg-gray-50/50",
                          !(formData.corporationInfo?.website) && "px-2 ring-1 ring-gray-200 bg-gray-50/50"
                        )}>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-muted-foreground font-medium mb-1">Website</div>
                            {editMode['corporationInfo.website'] ? (
                              <Input
                                value={formData.corporationInfo?.website || ''}
                                onChange={(e) => handleInputChange('corporationInfo.website', e.target.value)}
                                onBlur={() => handleBlur('corporationInfo.website')}
                                className="h-8 focus-visible:ring-2 focus-visible:ring-primary/60 text-sm"
                                placeholder="Website URL"
                                autoFocus
                              />
                            ) : (
                              <div
                                className={cn(
                                  "text-sm font-medium cursor-pointer rounded px-1 py-0.5 transition-colors min-h-[20px] flex items-center justify-between",
                                  "group-hover:bg-white",
                                  !(formData.corporationInfo?.website) && "text-gray-400"
                                )}
                                onClick={() => toggleEdit('corporationInfo.website')}
                              >
                                <span className="truncate">{formData.corporationInfo?.website || ''}</span>
                                {formData.corporationInfo?.website && isLinkEnabled('corporationInfo.website') && (
                                  <a
                                    href={formatWebsite(formData.corporationInfo.website)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-700 ml-2 text-xs"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Visit
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        )}

                        <div className="grid grid-cols-2 gap-2">
                          {shouldShowField('corporationInfo.businessType') && renderInlineField(
                            'corporationInfo.businessType',
                            'Business Structure',
                            <Building2 className="h-4 w-4 text-blue-500" />,
                            'select'
                          )}
                          {shouldShowField('corporationInfo.businessStartDate') && renderInlineField(
                            'corporationInfo.businessStartDate',
                            'Start Date',
                            <Calendar className="h-4 w-4 text-blue-500" />
                          )}
                        </div>

                        {/* Address Section - compact grid */}
                        <div className="space-y-1">
                          <div className="text-xs text-muted-foreground font-medium">Address</div>
                          <div className="grid grid-cols-1 gap-1">
                            {shouldShowField('corporationInfo.businessAddress.street') && renderInlineField(
                              'corporationInfo.businessAddress.street',
                              'Street',
                              <MapPin className="h-4 w-4 text-blue-500" />
                            )}
                            <div className="grid grid-cols-12 gap-2">
                              <div className="col-span-7">
                                {shouldShowField('corporationInfo.businessAddress.city') && renderInlineField('corporationInfo.businessAddress.city', 'City', null)}
                              </div>
                              <div className="col-span-2">
                                {shouldShowField('corporationInfo.businessAddress.state') && renderInlineField('corporationInfo.businessAddress.state', 'State', null)}
                              </div>
                              <div className="col-span-3">
                                {shouldShowField('corporationInfo.businessAddress.zipCode') && renderInlineField('corporationInfo.businessAddress.zipCode', 'ZIP', null)}
                              </div>
                            </div>
                          </div>
                        </div>

                        {shouldShowField('corporationInfo.businessDescription') && (
                          <div className="w-full">
                            {renderInlineField(
                              'corporationInfo.businessDescription',
                              'Business Description',
                              <FileText className="h-4 w-4 text-blue-500" />,
                              'textarea'
                            )}
                          </div>
                        )}

                        {/* SIC/MCC Code - Full Width */}
                        {shouldShowField('corporationInfo.sicCode') && (
                          <div className="w-full">
                            <SicCodeSelector
                              value={formData.corporationInfo?.sicCode || ''}
                              valueDescription={formData.corporationInfo?.sicDescription || ''}
                              onValueChange={async (sicCode) => {
                                const code = sicCode?.code || '';
                                const description = sicCode?.description || '';

                                handleInputChange('corporationInfo.sicCode', code);
                                handleInputChange('corporationInfo.sicDescription', description);

                                // Directly trigger mutation to ensure persistence since this component doesn't use edit mode
                                try {
                                  await updateFieldMutation.mutateAsync({
                                    field: 'corporationInfo.sicCode',
                                    value: code
                                  });
                                  await updateFieldMutation.mutateAsync({
                                    field: 'corporationInfo.sicDescription',
                                    value: description
                                  });
                                } catch (error) {
                                  console.error('Failed to save SIC code changes:', error);
                                  toast({
                                    title: 'Error saving SIC code',
                                    description: 'Changes might not be persistent. Please try again.',
                                    variant: 'destructive',
                                  });
                                }
                              }}
                              dynamicCodes={allEnabledCodes}
                              loading={industryCodesLoading}
                            />
                          </div>
                        )}

                        {/* Customer Service Section */}
                        <div className="space-y-1">
                          <div className="text-xs text-muted-foreground font-medium">Customer Service</div>
                          <div className="bg-gray-50/60 rounded-md p-2 space-y-1 ring-1 ring-gray-100">
                            {shouldShowField('corporationInfo.customerServiceEmail') && renderInlineField(
                              'corporationInfo.customerServiceEmail',
                              'Email',
                              <Mail className="h-4 w-4 text-blue-500" />,
                              'email'
                            )}
                            {shouldShowField('corporationInfo.customerServicePhone') && renderInlineField(
                              'corporationInfo.customerServicePhone',
                              'Phone',
                              <Phone className="h-4 w-4 text-blue-500" />,
                              'tel'
                            )}
                          </div>
                        </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Processing History Section */}
                <AccordionItem value="processingHistory">
                  <AccordionTrigger className="hover:no-underline py-2 px-4 rounded-md border border-transparent focus-visible:outline-none focus-visible:ring-0 data-[state=open]:ring-1 data-[state=open]:ring-blue-200 data-[state=open]:bg-gradient-to-r data-[state=open]:from-blue-50/50 data-[state=open]:to-indigo-50/50 transition-all">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-600/90 rounded-lg"><Clock className="h-3.5 w-3.5 text-white" /></div>
                      <span className="text-gray-900 text-sm font-semibold">Processing History</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-2">
                    <div className="space-y-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {shouldShowField('processingHistory.monthlyVolume') && renderInlineField(
                            'processingHistory.monthlyVolume',
                            'Monthly Volume',
                            <DollarSign className="h-4 w-4 text-green-500" />,
                            'number'
                          )}
                          {shouldShowField('processingHistory.averageTicket') && renderInlineField(
                            'processingHistory.averageTicket',
                            'Avg Ticket',
                            <TrendingUp className="h-4 w-4 text-green-500" />,
                            'number'
                          )}
                          {shouldShowField('processingHistory.highTicket') && renderInlineField(
                            'processingHistory.highTicket',
                            'High Ticket',
                            <CreditCard className="h-4 w-4 text-green-500" />,
                            'number'
                          )}
                          {shouldShowField('processingHistory.salesDeliveryDays') && renderInlineField(
                            'processingHistory.salesDeliveryDays',
                            'Avg Delivery (days)',
                            <Clock className="h-4 w-4 text-green-500" />,
                            'number'
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {shouldShowField('processingHistory.whenIsCustomerCharged') && renderInlineField(
                            'processingHistory.whenIsCustomerCharged',
                            'Charge Timing',
                            <Clock className="h-4 w-4 text-indigo-500" />,
                            'select',
                            [
                              { value: 'shipment', label: 'On Shipment' },
                              { value: 'delivery', label: 'On Delivery' },
                              { value: 'order', label: 'On Order' },
                              { value: 'other', label: 'Other' }
                            ]
                          )}
                          {shouldShowField('processingHistory.orderTurnaroundTime') && renderInlineField(
                            'processingHistory.orderTurnaroundTime',
                            'Order Turnaround',
                            <Clock className="h-4 w-4 text-blue-500" />,
                            'number'
                          )}
                          {shouldShowField('processingHistory.advancedDeposits') && renderInlineField(
                            'processingHistory.advancedDeposits',
                            'Advanced Deposits',
                            <CreditCard className="h-4 w-4 text-indigo-500" />,
                            'select'
                          )}
                          {shouldShowField('processingHistory.depositPct') && renderInlineField(
                            'processingHistory.depositPct',
                            'Deposit Percentage',
                            <Percent className="h-4 w-4 text-indigo-500" />,
                            'number'
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          {/* Auto-balance Card Present and E-commerce to sum to 100 */}
                          <div className="flex items-center gap-3 py-1 hover:bg-gray-50/50 rounded transition-colors group">
                            <div className="flex-shrink-0"><Percent className="h-4 w-4 text-blue-500" /></div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-muted-foreground font-medium mb-1">Card Present (%)</div>
                              {editMode['processingHistory.cardPresentPercentage'] ? (
                                <Input
                                  type="number"
                                  value={formData.processingHistory?.cardPresentPercentage || 0}
                                  onChange={(e) => {
                                    const value = Math.max(0, Math.min(100, Number(e.target.value)));
                                    handleInputChange('processingHistory.cardPresentPercentage', String(value));
                                    handleInputChange('processingHistory.ecommercePercentage', String(100 - value));
                                  }}
                                  onBlur={() => handleBlur('processingHistory.cardPresentPercentage')}
                                  onKeyDown={(e) => handleEditableKeyDown(e, 'processingHistory.cardPresentPercentage')}
                                  className="h-8 focus-visible:ring-1 focus-visible:ring-primary/50 text-sm"
                                  placeholder="Card Present (%)"
                                  autoFocus
                                />
                              ) : (
                                <div
                                  className="text-sm font-medium cursor-pointer rounded px-1 py-0.5 transition-colors group-hover:bg-white min-h-[20px] flex items-center"
                                  onClick={() => toggleEdit('processingHistory.cardPresentPercentage')}
                                >
                                  <span className="text-gray-900">{formData.processingHistory?.cardPresentPercentage || 0}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 py-1 hover:bg-gray-50/50 rounded transition-colors group">
                            <div className="flex-shrink-0"><Globe className="h-4 w-4 text-blue-500" /></div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-muted-foreground font-medium mb-1">E-commerce (%)</div>
                              {editMode['processingHistory.ecommercePercentage'] ? (
                                <Input
                                  type="number"
                                  value={formData.processingHistory?.ecommercePercentage || 0}
                                  onChange={(e) => {
                                    const value = Math.max(0, Math.min(100, Number(e.target.value)));
                                    handleInputChange('processingHistory.ecommercePercentage', String(value));
                                    handleInputChange('processingHistory.cardPresentPercentage', String(100 - value));
                                  }}
                                  onBlur={() => handleBlur('processingHistory.ecommercePercentage')}
                                  onKeyDown={(e) => handleEditableKeyDown(e, 'processingHistory.ecommercePercentage')}
                                  className="h-8 focus-visible:ring-2 focus-visible:ring-primary/60 text-sm"
                                  placeholder="E-commerce (%)"
                                  autoFocus
                                />
                              ) : (
                                <div
                                  className="text-sm font-medium cursor-pointer rounded px-1 py-0.5 transition-colors group-hover:bg-white min-h-[20px] flex items-center"
                                  onClick={() => toggleEdit('processingHistory.ecommercePercentage')}
                                >
                                  <span className="text-gray-900">{formData.processingHistory?.ecommercePercentage || 0}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {(shouldShowField('processingHistory.isCurrentlyProcessing') || shouldShowField('processingHistory.hasBeenTerminated')) ? (
                        <div className="grid grid-cols-2 gap-2">
                          <div className={cn(
                            "flex items-center gap-2 py-1 rounded transition-colors group",
                            "hover:bg-gray-50/50",
                            !(formData.processingHistory?.isCurrentlyProcessing) && "px-2 ring-1 ring-gray-200 bg-gray-50/50"
                          )}>
                            <div className="flex-shrink-0"><Zap className="h-4 w-4 text-purple-500" /></div>
                            <div className="flex items-center gap-2 flex-1">
                              <span className="text-xs text-muted-foreground font-medium">Currently Processing:</span>
                              {editMode['processingHistory.isCurrentlyProcessing'] ? (
                                <Select
                                  value={formData.processingHistory?.isCurrentlyProcessing || ''}
                                  onValueChange={(value) => {
                                    handleInputChange('processingHistory.isCurrentlyProcessing', value);
                                    // Directly save the new value to avoid undefined error from stale state
                                    updateFieldMutation.mutateAsync({ 
                                      field: 'processingHistory.isCurrentlyProcessing', 
                                      value 
                                    });
                                  }}
                                >
                                  <SelectTrigger className="h-7 w-20 focus-visible:ring-2 focus-visible:ring-primary/60">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="yes">Yes</SelectItem>
                                    <SelectItem value="no">No</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div
                                  className={cn(
                                    "text-sm font-medium cursor-pointer rounded px-1 py-0.5 transition-colors",
                                    "group-hover:bg-white",
                                    !(formData.processingHistory?.isCurrentlyProcessing) && "text-gray-400"
                                  )}
                                  onClick={() => toggleEdit('processingHistory.isCurrentlyProcessing')}
                                >
                                  {formData.processingHistory?.isCurrentlyProcessing === 'yes' ? 'Yes' : 
                                   formData.processingHistory?.isCurrentlyProcessing === 'no' ? 'No' : 'Select'}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className={cn(
                            "flex items-center gap-2 py-1 rounded transition-colors group",
                            "hover:bg-gray-50/50",
                            !(formData.processingHistory?.hasBeenTerminated) && "px-2 ring-1 ring-gray-200 bg-gray-50/50"
                          )}>
                            <div className="flex-shrink-0"><AlertTriangle className="h-4 w-4 text-red-500" /></div>
                            <div className="flex items-center gap-2 flex-1">
                              <span className="text-xs text-muted-foreground font-medium">Terminated:</span>
                              {editMode['processingHistory.hasBeenTerminated'] ? (
                                <Select
                                  value={formData.processingHistory?.hasBeenTerminated || ''}
                                  onValueChange={(value) => {
                                    handleInputChange('processingHistory.hasBeenTerminated', value);
                                    // Directly save the new value to avoid undefined error from stale state
                                    updateFieldMutation.mutateAsync({ 
                                      field: 'processingHistory.hasBeenTerminated', 
                                      value 
                                    });
                                  }}
                                >
                                  <SelectTrigger className="h-7 w-20 focus-visible:ring-2 focus-visible:ring-primary/60">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="yes">Yes</SelectItem>
                                    <SelectItem value="no">No</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div
                                  className={cn(
                                    "text-sm font-medium cursor-pointer rounded px-1 py-0.5 transition-colors",
                                    "group-hover:bg-white",
                                    !(formData.processingHistory?.hasBeenTerminated) && "text-gray-400"
                                  )}
                                  onClick={() => toggleEdit('processingHistory.hasBeenTerminated')}
                                >
                                  {formData.processingHistory?.hasBeenTerminated === 'yes' ? 'Yes' : 
                                   formData.processingHistory?.hasBeenTerminated === 'no' ? 'No' : 'Select'}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        ) : null}
                        
                        {shouldShowField('processingHistory.terminationExplanation') && renderInlineField(
                          'processingHistory.terminationExplanation',
                          'Termination Explanation',
                          <AlertTriangle className="h-4 w-4 text-red-500" />,
                          'textarea'
                        )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Bank Details Section */}
                <AccordionItem value="bankDetails">
                  <AccordionTrigger className="hover:no-underline py-2 px-4 rounded-md border border-transparent focus-visible:outline-none focus-visible:ring-0 data-[state=open]:ring-1 data-[state=open]:ring-blue-200 data-[state=open]:bg-gradient-to-r data-[state=open]:from-blue-50/50 data-[state=open]:to-indigo-50/50 transition-all">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-600/90 rounded-lg"><Landmark className="h-3.5 w-3.5 text-white" /></div>
                      <span className="text-gray-900 text-sm font-semibold">Bank Details</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-2">
                    <div className="space-y-1">
                        {shouldShowField('bankDetails.bankName') && renderInlineFieldOneLine(
                          'bankDetails.bankName',
                          'Bank Name',
                          <Landmark className="h-4 w-4 text-blue-500" />
                        )}
                        
                        <div className="grid grid-cols-2 gap-2">
                          {shouldShowField('bankDetails.routingNumber') && renderInlineField(
                            'bankDetails.routingNumber',
                            'Routing Number',
                            <Hash className="h-4 w-4 text-green-500" />
                          )}
                          
                          {shouldShowField('bankDetails.accountNumber') && renderInlineField(
                            'bankDetails.accountNumber',
                            'Account Number',
                            <CreditCard className="h-4 w-4 text-green-500" />
                          )}
                        </div>

                        {shouldShowField('bankDetails.accountType') && renderInlineField(
                          'bankDetails.accountType',
                          'Account Type',
                          <CreditCard className="h-4 w-4 text-blue-500" />,
                          'select'
                        )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Pricing Section */}
                <AccordionItem value="pricing">
                  <AccordionTrigger className="hover:no-underline py-2 px-4 rounded-md border border-transparent focus-visible:outline-none focus-visible:ring-0 data-[state=open]:ring-1 data-[state=open]:ring-blue-200 data-[state=open]:bg-gradient-to-r data-[state=open]:from-blue-50/50 data-[state=open]:to-indigo-50/50 transition-all">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-600/90 rounded-lg"><DollarSign className="h-3.5 w-3.5 text-white" /></div>
                      <span className="text-gray-900 text-sm font-semibold">Pricing</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-2">
                    <div className="space-y-2">
                        {/* Pricing Template Selector */}
                        <div className="bg-yellow-50/50 rounded p-3 border border-yellow-200">
                          <div className="text-xs text-yellow-800 font-medium mb-2">Pricing Template</div>
                          <div className="space-y-2">
                            {/* Banking Partner Selector */}
                            <div>
                              <Label className="text-xs text-gray-600 mb-1">Banking Partner</Label>
                              <Select
                                value={formData.selectedBankingPartnerId || undefined}
                                onValueChange={async (value) => {
                                  const newValue = value === '__NONE__' ? undefined : value;

                                  // Update form data immediately for UI responsiveness
                                  setFormData(prev => ({
                                    ...prev,
                                    selectedBankingPartnerId: newValue,
                                    pricingTemplateId: undefined, // Reset template when partner changes
                                  }));

                                  // Perform database operations
                                  try {
                                    // If a bank is selected, add it to assignedBanks first
                                    if (newValue && applicationId) {
                                      const currentBanks = application?.assignedBanks || [];
                                      if (!currentBanks.includes(newValue)) {
                                        await MerchantApplicationService.addBankToApplication(applicationId, newValue);
                                        toast({
                                          title: 'Banking partner added',
                                          description: 'The banking partner has been added to this application',
                                        });
                                      }
                                    }

                                    // Save the selectedBankingPartnerId to data field
                                    await updateFieldMutation.mutateAsync({
                                      field: 'selectedBankingPartnerId',
                                      value: newValue
                                    });
                                  } catch (error) {
                                    console.error('Error updating banking partner:', error);
                                    toast({
                                      title: 'Error',
                                      description: 'Failed to update banking partner',
                                      variant: 'destructive',
                                    });
                                  }
                                }}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Select banking partner" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__NONE__">None</SelectItem>
                                  {bankingPartners.map((partner: any) => (
                                    <SelectItem key={partner.id} value={partner.id}>
                                      {partner.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Pricing Template Selector */}
                            {formData.selectedBankingPartnerId && (
                              <div>
                                <Label className="text-xs text-gray-600 mb-1">Pricing Template</Label>
                                <Select
                                  value={formData.pricingTemplateId || undefined}
                                  onValueChange={async (value) => {
                                    const newValue = value === '__NONE__' ? undefined : value;

                                    if (newValue) {
                                      // Find the selected template
                                      const template = pricingTemplates.find((t: any) => t.id === newValue);
                                      if (template) {
                                        // Update pricing fields with template values
                                        const updatedPricing = {
                                          online: {
                                            processingFee: template.pricing.processingFees.visaMasterDiscover,
                                            transactionFee: template.pricing.transactionFees.visaMasterDiscover,
                                          },
                                          offline: {
                                            processingFee: template.pricing.processingFees.visaMasterDiscover,
                                            transactionFee: template.pricing.transactionFees.visaMasterDiscover,
                                          },
                                          chargebackFee: template.pricing.chargebackFee,
                                          monthlyMinimum: template.pricing.monthlyMinimumFee,
                                        };

                                        // Update form data immediately for UI responsiveness
                                        setFormData(prev => ({
                                          ...prev,
                                          pricingTemplateId: newValue,
                                          pricing: updatedPricing,
                                        }));

                                        // Save both fields in a single batch using the data object
                                        try {
                                          await updateFieldMutation.mutateAsync({
                                            field: 'pricingTemplateId',
                                            value: newValue
                                          });
                                          await updateFieldMutation.mutateAsync({
                                            field: 'pricing',
                                            value: updatedPricing
                                          });
                                        } catch (error) {
                                          console.error('Error updating pricing template:', error);
                                          toast({
                                            title: 'Error',
                                            description: 'Failed to update pricing template',
                                            variant: 'destructive',
                                          });
                                        }
                                      }
                                    } else {
                                      // "None" selected - only clear template ID, keep existing pricing values
                                      setFormData(prev => ({
                                        ...prev,
                                        pricingTemplateId: undefined,
                                        // Keep existing pricing values (don't modify them)
                                      }));

                                      try {
                                        await updateFieldMutation.mutateAsync({
                                          field: 'pricingTemplateId',
                                          value: undefined
                                        });
                                      } catch (error) {
                                        console.error('Error clearing pricing template:', error);
                                      }
                                    }
                                  }}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Select pricing template" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__NONE__">None (Manual Entry)</SelectItem>
                                    {pricingTemplates.map((template: any) => (
                                      <SelectItem key={template.id} value={template.id}>
                                        {template.name} ({template.riskType})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {formData.pricingTemplateId && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Template applied. You can still adjust values below.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Online Pricing */}
                        <div className="bg-blue-50/50 rounded p-2">
                          <div className="text-xs text-blue-700 font-medium mb-1">Online Pricing</div>
                          <div className="grid grid-cols-2 gap-2">
                            {renderInlineField(
                              'pricing.online.processingFee',
                              'Processing Fee (%)',
                              <Percent className="h-4 w-4 text-green-500" />,
                              'number'
                            )}
                            {renderInlineField(
                              'pricing.online.transactionFee',
                              'Transaction Fee (¢)',
                              <CreditCard className="h-4 w-4 text-green-500" />,
                              'number'
                            )}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Total: {formData.pricing?.online?.processingFee || 0}% + ${((formData.pricing?.online?.transactionFee || 0) / 100).toFixed(2)}
                          </div>
                        </div>

                        {/* Offline Pricing */}
                        <div className="bg-purple-50/50 rounded p-2">
                          <div className="text-xs text-purple-700 font-medium mb-1">Offline Pricing</div>
                          <div className="grid grid-cols-2 gap-2">
                            {renderInlineField(
                              'pricing.offline.processingFee',
                              'Processing Fee (%)',
                              <Percent className="h-4 w-4 text-green-500" />,
                              'number'
                            )}
                            {renderInlineField(
                              'pricing.offline.transactionFee',
                              'Transaction Fee (¢)',
                              <CreditCard className="h-4 w-4 text-green-500" />,
                              'number'
                            )}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Total: {formData.pricing?.offline?.processingFee || 0}% + ${((formData.pricing?.offline?.transactionFee || 0) / 100).toFixed(2)}
                          </div>
                        </div>
                        
                        {/* Additional Fees Section */}
                        <div className="bg-green-50/50 rounded p-2">
                          <div className="text-xs text-green-700 font-medium mb-1">Additional Fees</div>
                          <div className="grid grid-cols-2 gap-2">
                            {renderInlineField(
                              'pricing.chargebackFee',
                              'Chargeback Fee ($)',
                              <CreditCard className="h-4 w-4 text-green-500" />,
                              'number'
                            )}
                            {renderInlineField(
                              'pricing.monthlyMinimum',
                              'Monthly Minimum ($)',
                              <DollarSign className="h-4 w-4 text-green-500" />,
                              'number'
                            )}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Chargeback: ${formData.pricing?.chargebackFee || 0} | Monthly Min: ${formData.pricing?.monthlyMinimum || 0}
                          </div>
                        </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Beneficial Owners Section */}
                <AccordionItem value="beneficialOwners">
                  <AccordionTrigger className="hover:no-underline py-2 px-4 rounded-md border border-transparent focus-visible:outline-none focus-visible:ring-0 data-[state=open]:ring-1 data-[state=open]:ring-blue-200 data-[state=open]:bg-gradient-to-r data-[state=open]:from-blue-50/50 data-[state=open]:to-indigo-50/50 transition-all">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-indigo-600/90 rounded-lg"><Shield className="h-3.5 w-3.5 text-white" /></div>
                        <span className="text-gray-900 text-sm font-semibold">Owners</span>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn('px-2 py-0.5 text-xs', getOwnershipColor(totalOwnership))}
                      >
                        {totalOwnership}%
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-2">
                    <BeneficialOwnersDisplay
                      beneficialOwners={formData.beneficialOwners || []}
                      onOwnerChange={(index, field, value) => {
                        const updatedOwners = [...(formData.beneficialOwners || [])];
                        updatedOwners[index] = {
                          ...updatedOwners[index],
                          [field]: value,
                        };
                        setFormData((prev) => ({
                          ...prev,
                          beneficialOwners: updatedOwners,
                        }));
                      }}
                      onSave={async () => {
                        await updateFieldMutation.mutateAsync({
                          field: 'beneficialOwners',
                          value: formData.beneficialOwners,
                        });
                      }}
                      onAddOwner={() => {
                        if (application?.data?.applicationType === 'individual') return;
                        setFormData((prev) => {
                          const currentOwners = prev.beneficialOwners || [];
                          const currentSum = currentOwners.reduce(
                            (sum: number, owner: BeneficialOwner) => sum + (Number(owner.ownershipPercentage) || 0),
                            0
                          );
                          const remaining = Math.max(0, 100 - currentSum);
                          const newOwner: BeneficialOwner = {
                            firstName: '',
                            lastName: '',
                            title: 'Owner',
                            dateOfBirth: '',
                            ssn: '',
                            address: '',
                            city: '',
                            state: '',
                            zipCode: '',
                            phone: '',
                            email: '',
                            ownershipPercentage: String(remaining),
                          } as BeneficialOwner;
                          return {
                            ...prev,
                            beneficialOwners: [
                              ...currentOwners,
                              newOwner,
                            ],
                          };
                        });
                      }}
                      onRemoveOwner={(index) => {
                        if (application?.data?.applicationType === 'individual') return;
                        setFormData((prev) => ({
                          ...prev,
                          beneficialOwners: prev.beneficialOwners?.filter((_, i) => i !== index) || [],
                        }));
                      }}
                    />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Other Section */}
              <div className="mt-3">
                <Accordion type="single" collapsible>
                  <AccordionItem value="other">
                    <AccordionTrigger className="hover:no-underline py-2 px-4 rounded-md border border-transparent focus-visible:outline-none focus-visible:ring-0 data-[state=open]:ring-1 data-[state=open]:ring-blue-200 data-[state=open]:bg-gradient-to-r data-[state=open]:from-blue-50/50 data-[state=open]:to-indigo-50/50 transition-all">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-slate-600/90 rounded-lg"><Info className="h-3.5 w-3.5 text-white" /></div>
                        <span className="text-gray-900 text-sm font-semibold">Other Details</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 pt-2">
                      <div className="rounded-xl border border-slate-200/80 bg-white/80 p-4 shadow-sm">
                        <div className="flex flex-col gap-5">
                          <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                            <div className="min-w-[160px] text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/80">
                              Merchant Category
                            </div>
                            <div className="flex-1 min-w-[220px]">
                              {editMode['other.merchantCategory'] ? (
                                <Select
                                  value={formData.other?.merchantCategory || 'retail'}
                                  onValueChange={(value) => {
                                    handleInputChange('other.merchantCategory', value);
                                    setTimeout(() => handleBlur('other.merchantCategory'), 100);
                                  }}
                                >
                                  <SelectTrigger className="h-9 w-full text-sm shadow-sm focus-visible:ring-2 focus-visible:ring-primary/60">
                                    <SelectValue placeholder="Select category" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="ecommerce">E-Commerce</SelectItem>
                                    <SelectItem value="moto">Moto</SelectItem>
                                    <SelectItem value="retail">Retail</SelectItem>
                                    <SelectItem value="restaurant">Restaurant</SelectItem>
                                    <SelectItem value="hotel">Hotel</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div
                                  className="inline-flex cursor-pointer items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-semibold capitalize text-slate-700 shadow-sm transition-colors hover:border-primary/60 hover:bg-primary/5"
                                  onClick={() => toggleEdit('other.merchantCategory')}
                                >
                                  {formData.other?.merchantCategory || 'retail'}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Banking Partner Selection - Moved below Category */}
                          <div className="flex flex-col gap-3">
                            <div className="w-full md:max-w-[420px]">
                              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/80">
                                Banking Partner
                              </div>
                              <div className="mt-2 flex gap-2">
                                <Select onValueChange={setSelectedBankId}>
                                  <SelectTrigger className="flex-1 h-9 text-sm shadow-sm focus-visible:ring-2 focus-visible:ring-primary/60">
                                    <SelectValue placeholder="Select a bank" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {bankingPartners.map((partner) => (
                                      <SelectItem key={partner.id} value={partner.id}>
                                        <span style={{ color: partner.color }}>{partner.name}</span>
                                      </SelectItem>
                                    ))}
                                    {bankingPartners.length === 0 && (
                                      <div className="py-3 px-4 text-sm text-gray-500">
                                        No active banking partners found
                                      </div>
                                    )}
                                  </SelectContent>
                                </Select>
                                <Button
                                  disabled={!selectedBankId}
                                  className="h-9 px-3 text-sm shadow-sm disabled:shadow-none"
                                  onClick={handleAddBank}
                                >
                                  <Plus className="w-3.5 h-3.5 mr-1" />
                                  Add
                                </Button>
                              </div>

                              {/* Display assigned banks */}
                              {application?.assignedBanks && application.assignedBanks.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {application.assignedBanks.map((bankId) => {
                                    const bank = bankingPartners.find((p) => p.id === bankId);
                                    return (
                                      <div
                                        key={bankId}
                                        className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold shadow-sm"
                                        style={{
                                          borderColor: bank?.color || '#cbd5e1',
                                          backgroundColor: bank ? `${bank.color}15` : '#f1f5f9',
                                          color: bank?.color || '#64748b'
                                        }}
                                      >
                                        {bank?.name || 'Unknown Bank'}
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                                          onClick={() => handleRemoveBank(bankId)}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Validate Application Button - Replaces "Send to Bank" */}
                          <div className="flex flex-col gap-3">
                            {(() => {
                              const isPayArcAssigned = application?.assignedBanks?.some((bankId) => {
                                const bank = bankingPartners.find((p) => p.id === bankId);
                                return bank?.name.toLowerCase().includes('payarc');
                              });
                              const payArcBank = application?.assignedBanks?.find((bankId) => {
                                const bank = bankingPartners.find((p) => p.id === bankId);
                                return bank?.name.toLowerCase().includes('payarc');
                              });
                              const payArcBankName = payArcBank
                                ? bankingPartners.find((p) => p.id === payArcBank)?.name
                                : null;
                              const hasPayArcData = formData.payArcMerchantCode;

                              if (isPayArcAssigned) {
                                return (
                                  <div className="flex flex-col gap-4">
                                    {/* Validate Application Button */}
                                    <div className="flex flex-col gap-2">
                                      <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
                                        <Button
                                          variant="default"
                                          className={cn(
                                            'h-9 text-sm shadow-sm',
                                            hasPayArcData
                                              ? 'bg-emerald-600 hover:bg-emerald-700'
                                              : 'bg-blue-600 hover:bg-blue-700'
                                          )}
                                          onClick={handleSendToPayArc}
                                          disabled={isPayArcSending}
                                        >
                                          <Send className="w-3.5 h-3.5 mr-1" />
                                          {isPayArcSending ? 'Validating...' : hasPayArcData ? 'Validated ✓' : 'Validate Application'}
                                        </Button>
                                        {payArcBankName && (
                                          <span className="text-sm font-semibold text-slate-600">
                                            {payArcBankName}
                                          </span>
                                        )}
                                      </div>
                                      <Button
                                        variant="outline"
                                        className="w-full h-9 text-sm shadow-sm md:w-auto"
                                        onClick={handleExaminePayArc}
                                      >
                                        <Eye className="w-3.5 h-3.5 mr-1" />
                                        Examine Call to PAYARC
                                      </Button>
                                    </div>

                                    {/* PayArc Merchant Code Display - Only shown after validation */}
                                    {hasPayArcData && (
                                      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200/70 bg-emerald-50/70 px-3 py-3 shadow-inner">
                                        <div className="min-w-[160px] text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                                          PayArc Merchant Code
                                        </div>
                                        <div className="flex flex-1 min-w-[240px] items-center gap-2">
                                          <code className="flex-1 truncate rounded-md border border-emerald-200 bg-white px-3 py-1.5 text-sm font-semibold text-emerald-700 shadow-sm">
                                            {formData.payArcMerchantCode}
                                          </code>
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button
                                                  type="button"
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-8 w-8 border border-transparent text-emerald-600 transition-colors hover:border-emerald-200 hover:bg-emerald-100/70 hover:text-emerald-700"
                                                  onClick={handleCopyPayArcCode}
                                                  aria-label="Copy PayArc Merchant Code"
                                                >
                                                  <Copy className="h-4 w-4" />
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent side="top">Copy code</TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        </div>
                                        {formData.payArcSubmittedAt && (
                                          <div className="flex items-center gap-2 text-xs font-medium text-emerald-700">
                                            <Clock className="h-3.5 w-3.5" />
                                            <span>
                                              Validated {formData.payArcSubmittedAt instanceof Date
                                                ? formData.payArcSubmittedAt.toLocaleDateString()
                                                : formData.payArcSubmittedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Submit Application Button - Shown under validation button */}
                                    {hasPayArcData && (
                                      <div className="flex flex-col gap-2 w-full md:w-auto">
                                        <Button
                                          className="h-9 px-3 text-sm shadow-sm w-full md:w-auto"
                                          onClick={handleSubmitPayArcApplication}
                                          disabled={isSubmittingPayArc || isPayArcSending}
                                        >
                                          <FileText className="w-3.5 h-3.5 mr-1" />
                                          {isSubmittingPayArc ? 'Submitting…' : 'Submit Application'}
                                        </Button>
                                        <Button
                                          variant="outline"
                                          className="h-9 px-3 text-sm shadow-sm w-full md:w-auto"
                                          onClick={handleExamineSubmission}
                                          disabled={isSubmittingPayArc || isPayArcSending}
                                        >
                                          <Eye className="w-3.5 h-3.5 mr-1" />
                                          Examine Submission
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>

                          {sortedIntegrationLogs.length > 0 && (
                            <div className="border-t border-slate-200/70 pt-4">
                              <div className="flex items-center justify-between">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/80">
                                  Integration Activity
                                </div>
                                <span className="text-[11px] text-muted-foreground/70">
                                  {sortedIntegrationLogs.length} entr{sortedIntegrationLogs.length === 1 ? 'y' : 'ies'}
                                </span>
                              </div>
                              <div className="mt-2 space-y-2">
                                {sortedIntegrationLogs.map((log, index) => {
                                  const isSuccess = log.status === 'success';
                                  let timestampLabel = log.timestamp;
                                  try {
                                    timestampLabel = format(new Date(log.timestamp), 'MMM d, yyyy h:mm a');
                                  } catch {
                                    // ignore formatting errors
                                  }
                                  const sourceLabel = log.source === 'payarc-submit'
                                    ? 'PayArc Submit'
                                    : log.source === 'payarc-lead'
                                    ? 'PayArc Lead'
                                    : log.source;
                                  return (
                                    <div
                                      key={`${log.timestamp}-${index}`}
                                      className="flex items-start gap-3 rounded-lg border border-slate-200/70 bg-slate-50/60 px-3 py-2"
                                    >
                                      <Badge
                                        className={cn(
                                          'px-2.5 py-0.5 text-[11px] font-semibold',
                                          isSuccess
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : 'bg-rose-100 text-rose-700'
                                        )}
                                      >
                                        {isSuccess ? 'Success' : 'Error'}
                                      </Badge>
                                      <div className="flex-1 space-y-2">
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="text-sm font-medium text-slate-800">
                                            {log.message}
                                          </div>
                                          <div className="flex items-center gap-2">
                                            {log.details && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-slate-200 text-slate-600 hover:text-slate-800"
                                                onClick={() => {
                                                  setSelectedLog(log);
                                                  setIsLogDialogOpen(true);
                                                }}
                                              >
                                                View details
                                              </Button>
                                            )}
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="text-slate-500 hover:text-red-600"
                                              onClick={() => {
                                                const confirmed = window.confirm('Remove this integration log?');
                                                if (!confirmed) return;
                                                deleteIntegrationLog(log);
                                              }}
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </div>
                                        </div>
                                        <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground/80">
                                          {sourceLabel}
                                          {log.actor ? ` · ${log.actor}` : ''}
                                          {` · ${timestampLabel}`}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

            </CardContent>
          </Card>

          {/* Pipeline Status Dropdown */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-3">
              <Label className="text-xs font-medium text-gray-600 mb-2 block">Change Pipeline Status</Label>
              <Select
                value={application?.pipelineStatus || 'lead'}
                onValueChange={async (value: string) => {
                  if (!applicationId) return;
                  try {
                    const docRef = doc(db, 'merchantApplications', applicationId);
                    await updateDoc(docRef, {
                      pipelineStatus: value,
                      updatedAt: serverTimestamp()
                    });

                    // Also update company record if linked
                    if (application?.companyId) {
                      try {
                        const companyRef = doc(db, 'companies', application.companyId);
                        await updateDoc(companyRef, {
                          'merchantApplication.pipelineStatus': value,
                          'merchantApplication.updatedAt': serverTimestamp()
                        });
                      } catch (err) {
                        console.warn('Could not update company pipeline status', err);
                      }
                    }

                    toast({
                      title: 'Status Updated',
                      description: `Application moved to ${value.replace(/_/g, ' ')} stage`,
                    });
                    queryClient.invalidateQueries({ queryKey: ['merchantApplication', applicationId] });
                  } catch (error) {
                    console.error('Failed to update pipeline status:', error);
                    toast({
                      title: 'Error',
                      description: 'Failed to update status',
                      variant: 'destructive',
                    });
                  }
                }}
              >
                <SelectTrigger className="h-9 text-sm border-gray-200 shadow-sm ring-1 ring-gray-200 bg-white data-[state=open]:ring-2 data-[state=open]:ring-blue-500/20">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PIPELINE_COLORS).map(([status, color]) => (
                    <SelectItem key={status} value={status}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        <span className="capitalize">{status.replace(/_/g, ' ')}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Quick Actions Bar - Moved to Bottom */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-sm hover:shadow-md transition-all duration-300">
            <CardContent className="p-3">
              <div className="flex gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-sm"
                        onClick={() => {
                          toast({ title: 'Saving...', description: 'All changes saved successfully' });
                        }}
                      >
                        <Save className="h-3.5 w-3.5 mr-1.5" />
                        Save All
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Save all visible changes</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-blue-300 hover:bg-blue-50"
                        onClick={() => {
                          toast({ title: 'Exporting...', description: 'Application exported' });
                        }}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Export
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Export application snapshot</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {/* SendToPayArc button appears conditionally elsewhere */}
              </div>
            </CardContent>
          </Card>

          {/* Delete Application */}
          <Card className="bg-red-50 border-red-200 shadow-sm">
            <CardContent className="p-3">
              <Button
                variant="destructive"
                className="w-full h-9 text-sm font-medium bg-red-600 hover:bg-red-700"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" />
                Delete Application
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="flex-1 min-w-[500px]">
          <Card className="h-full flex flex-col">
            <Tabs defaultValue="emails" className="flex-1">
              <div className="border-b sticky top-0 z-10 bg-white/80 backdrop-blur supports-backdrop-blur:bg-white/60">
                <div className="px-4 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400 scroll-smooth">
                  <TabsList className="h-12 w-max justify-start gap-4 bg-transparent flex-nowrap">
                    <TabsTrigger
                      value="emails"
                      className="data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent px-2 rounded-none flex-shrink-0 whitespace-nowrap"
                    >
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <span>Emails</span>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger
                      value="phone"
                      className="data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent px-2 rounded-none flex-shrink-0 whitespace-nowrap"
                    >
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <span>Phone Calls</span>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger
                      value="notes"
                      className="data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent px-2 rounded-none flex-shrink-0 whitespace-nowrap"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span>Internal Notes</span>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger
                      value="documents"
                      className="data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent px-2 rounded-none flex-shrink-0 whitespace-nowrap"
                    >
                      <div className="flex items-center gap-2">
                        <FileIcon className="h-4 w-4" />
                        <span>Documents</span>
                      </div>
                    </TabsTrigger>
                    {application?.assignedBanks && application.assignedBanks.length > 0 && (
                      <TabsTrigger
                        value="banks"
                        className="data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent px-2 rounded-none flex-shrink-0 whitespace-nowrap"
                      >
                        <div className="flex items-center gap-2">
                          <Landmark className="h-4 w-4" />
                          <span>Banks</span>
                        </div>
                      </TabsTrigger>
                    )}
                    <TabsTrigger
                      value="processing"
                      className="data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent px-2 rounded-none flex-shrink-0 whitespace-nowrap"
                    >
                      <div className="flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        <span>Processing</span>
                      </div>
                    </TabsTrigger>
                  </TabsList>
                </div>
              </div>
              <div className="flex-1 p-6">
                <TabsContent value="emails" className="mt-0 border-none p-0">
                  <CommunicationsSection
                    applicationId={applicationId!}
                    tab="emails"
                    merchantName={application?.data?.corporationInfo?.businessName}
                    merchantEmail={application?.data?.corporationInfo?.customerServiceEmail}
                    businessName={application?.data?.corporationInfo?.businessName}
                    applicationStatus={application?.pipelineStatus}
                    bankingPartner={bankingPartners.find(p => p.id === formData.selectedBankingPartnerId)?.name}
                  />
                </TabsContent>
                <TabsContent value="phone" className="mt-0 border-none p-0">
                  <CommunicationsSection 
                    applicationId={applicationId!} 
                    tab="phone" 
                    merchantName={application?.data?.corporationInfo?.businessName}
                    merchantEmail={application?.data?.corporationInfo?.customerServiceEmail}
                  />
                </TabsContent>
                <TabsContent value="notes" className="mt-0 border-none p-0">
                  <CommunicationsSection 
                    applicationId={applicationId!} 
                    tab="notes" 
                    merchantName={application?.data?.corporationInfo?.businessName}
                    merchantEmail={application?.data?.corporationInfo?.customerServiceEmail}
                  />
                </TabsContent>
                <TabsContent value="documents" className="mt-0 border-none p-0">
        <div className="space-y-4">
                    {/* Action Buttons */}
                    <div className="flex gap-2 justify-end">
                      <AdditionalDocumentRequest
                        applicationId={applicationId!}
                        merchantName={application?.data?.corporationInfo?.businessName || 'Merchant'}
                      />
                      <SendToPayArcButton
                        merchantCode={(application as any)?.payarcMerchantCode || ''}
                        documents={(application as any)?.documents || {}}
                        disabled={!(application as any)?.payarcMerchantCode}
                      />
                    </div>
                    
                    {/* Documents Viewer */}
                    {(() => {
                      // Documents might be stored in data or at root level
                      const appData = application?.data || {};
                      
                      // CRITICAL FIX: Force display of known documents from Firestore
                      // We know these exist based on direct Firestore query
                      const docs: any = {
                        bank_statements: [],
                        drivers_license: [],
                        voided_check: [],
                        tax_returns: [],
                        business_license: [],
                        processing_statements: [],
                        financial_statements: [],
                        lease_agreement: [],
                        articles_of_incorporation: [],
                        payarc_terms: [],
                        other: []
                      };
                      
                      // List of all possible document keys
                      const documentKeys = [
                        'bank_statements',
                        'drivers_license', 
                        'voided_check',
                        'tax_returns',
                        'business_license',
                        'processing_statements',
                        'financial_statements',
                        'lease_agreement',
                        'articles_of_incorporation',
                        'payarc_terms'
                      ];
                      
                      // Check each key across possible locations: data, data.documents, root, root.documents
                      documentKeys.forEach(key => {
                        const collected: any[] = [];

                        // In application.data (legacy flat structure)
                        if (appData && (appData as any)[key]) {
                          const val = (appData as any)[key];
                          if (Array.isArray(val)) {
                            collected.push(...val);
                          } else {
                            collected.push(val);
                          }
                        }

                        // In application.data.documents (nested structure)
                        if (appData && (appData as any).documents && (appData as any).documents[key]) {
                          const val = (appData as any).documents[key];
                          if (Array.isArray(val)) {
                            collected.push(...val);
                          } else {
                            collected.push(val);
                          }
                        }

                        // At root level on application (very old structure)
                        if (application && (application as any)[key]) {
                          const val = (application as any)[key];
                          if (Array.isArray(val)) {
                            collected.push(...val);
                          } else {
                            collected.push(val);
                          }
                        }

                        // In application.documents (current preferred structure)
                        if (application && (application as any).documents && (application as any).documents[key]) {
                          const val = (application as any).documents[key];
                          if (Array.isArray(val)) {
                            collected.push(...val);
                          } else {
                            collected.push(val);
                          }
                        }

                        if (collected.length > 0) {
                          docs[key] = collected;
                        }
                      });
                      
                      // Special handling for "other" documents with defensive normalization
                      const ensureArray = (v: any): any[] => {
                        if (!v) return [];
                        return Array.isArray(v) ? v : [v];
                      };
                      docs.other = [
                        ...ensureArray((appData as any)?.other_documents),
                        ...ensureArray((appData as any)?.other),
                        ...ensureArray((appData as any)?.documents?.other),
                        ...ensureArray((application as any)?.other_documents),
                        ...ensureArray((application as any)?.other),
                        ...ensureArray((application as any)?.documents?.other),
                        ...ensureArray((application as any)?.additionalDocuments)
                      ].filter(Boolean);
                      
                      
                      return (
                        <DocumentsViewer
                          documents={docs}
                          companyId={user?.companyId || ''}
                        />
                      );
                    })()}
                  </div>
                </TabsContent>

                {application?.assignedBanks && application.assignedBanks.length > 0 && (
                  <TabsContent value="banks" className="mt-0 border-none p-0">
                    <div className="space-y-6">
                      {/* MID and Bearer Key Display */}
                      {(application as any)?.mid && (
                        <Card className="border-green-200 bg-green-50">
                          <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <CreditCard className="h-5 w-5 text-green-600" />
                                <div>
                                  <p className="text-sm text-green-700 font-medium">Merchant ID (MID)</p>
                                  <div className="flex items-center gap-2">
                                    <p className="text-lg font-mono font-semibold text-green-800">{(application as any).mid}</p>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0"
                                            onClick={() => {
                                              navigator.clipboard.writeText((application as any).mid || '');
                                              toast({ title: 'Copied!', description: 'MID copied to clipboard' });
                                            }}
                                          >
                                            <Copy className="h-3 w-3" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Copy MID</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                </div>
                              </div>
                              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                                {application.pipelineStatus === 'live' ? 'Live' : 'Approved'}
                              </Badge>
                            </div>

                            {/* Bearer Key Section */}
                            <div className="mt-4 pt-4 border-t border-green-200">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Key className="h-5 w-5 text-amber-600" />
                                  <div className="flex-1">
                                    <p className="text-sm text-green-700 font-medium">Bearer Key (API Token)</p>
                                    {(application as any)?.bearerKey ? (
                                      <div className="flex items-center gap-2 mt-1">
                                        <code className="text-xs font-mono text-green-800 bg-green-100 px-2 py-1 rounded max-w-[200px] truncate">
                                          {(application as any).bearerKey.substring(0, 20)}...
                                        </code>
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0"
                                                onClick={() => {
                                                  navigator.clipboard.writeText((application as any).bearerKey || '');
                                                  toast({ title: 'Copied!', description: 'Bearer key copied to clipboard' });
                                                }}
                                              >
                                                <Copy className="h-3 w-3" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Copy Bearer Key</TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-amber-600 mt-1">Not yet retrieved</p>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleRetrieveBearerKey}
                                  disabled={isRetrievingBearerKey}
                                  className="bg-white hover:bg-green-50 border-green-300"
                                >
                                  {isRetrievingBearerKey ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Retrieving...
                                    </>
                                  ) : (application as any)?.bearerKey ? (
                                    <>
                                      <RefreshCw className="h-4 w-4 mr-2" />
                                      Refresh Key
                                    </>
                                  ) : (
                                    <>
                                      <Key className="h-4 w-4 mr-2" />
                                      Get Bearer Key
                                    </>
                                  )}
                                </Button>
                              </div>
                              {(application as any)?.bearerKeyUpdatedAt && (
                                <p className="text-xs text-green-600 mt-2">
                                  Last updated: {format(
                                    typeof (application as any).bearerKeyUpdatedAt?.toDate === 'function'
                                      ? (application as any).bearerKeyUpdatedAt.toDate()
                                      : new Date((application as any).bearerKeyUpdatedAt),
                                    'MMM d, yyyy h:mm a'
                                  )}
                                </p>
                              )}
                            </div>

                            {/* Reserve Information Section */}
                            {(application as any)?.reserve && (
                              <div className="mt-4 pt-4 border-t border-green-200">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="h-5 w-5 text-blue-600">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-13c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z"/></svg>
                                  </div>
                                  <p className="text-sm text-green-700 font-medium">Reserve Information</p>
                                </div>
                                <div className="grid grid-cols-2 gap-3 ml-8">
                                  {(application as any).reserve?.percent !== undefined && (
                                    <div>
                                      <p className="text-xs text-gray-600">Reserve Percent</p>
                                      <p className="text-sm font-semibold text-gray-800">{(application as any).reserve.percent}%</p>
                                    </div>
                                  )}
                                  {(application as any).reserve?.rollingEnabled !== null && (application as any).reserve?.rollingEnabled !== undefined && (
                                    <div>
                                      <p className="text-xs text-gray-600">Rolling Reserve</p>
                                      <p className="text-sm font-semibold text-gray-800">{(application as any).reserve.rollingEnabled ? 'Enabled' : 'Disabled'}</p>
                                    </div>
                                  )}
                                  {(application as any).reserve?.rollingDays && (
                                    <div>
                                      <p className="text-xs text-gray-600">Rolling Days</p>
                                      <p className="text-sm font-semibold text-gray-800">{(application as any).reserve.rollingDays} days</p>
                                    </div>
                                  )}
                                  {(application as any).reserve?.capAmount && (
                                    <div>
                                      <p className="text-xs text-gray-600">Reserve Cap</p>
                                      <p className="text-sm font-semibold text-gray-800">${(application as any).reserve.capAmount?.toLocaleString()}</p>
                                    </div>
                                  )}
                                  {(application as any).reserve?.dailyAmount && (
                                    <div>
                                      <p className="text-xs text-gray-600">Daily Amount</p>
                                      <p className="text-sm font-semibold text-gray-800">${(application as any).reserve.dailyAmount?.toLocaleString()}</p>
                                    </div>
                                  )}
                                  {(application as any).reserve?.collectionType && (
                                    <div>
                                      <p className="text-xs text-gray-600">Collection Type</p>
                                      <p className="text-sm font-semibold text-gray-800">{(application as any).reserve.collectionType}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {(application as any)?.approvedAt && (
                              <p className="text-xs text-green-600 mt-4">
                                Approved: {format(
                                  typeof (application as any).approvedAt?.toDate === 'function'
                                    ? (application as any).approvedAt.toDate()
                                    : new Date((application as any).approvedAt),
                                  'MMM d, yyyy h:mm a'
                                )}
                              </p>
                            )}
                            {(application as any)?.liveAt && (
                              <p className="text-xs text-green-600 mt-1">
                                Went Live: {format(
                                  typeof (application as any).liveAt?.toDate === 'function'
                                    ? (application as any).liveAt.toDate()
                                    : new Date((application as any).liveAt),
                                  'MMM d, yyyy h:mm a'
                                )}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Banking Partners */}
                      {application.assignedBanks.map((bankId) => {
                        const bank = bankingPartners.find(p => p.id === bankId);
                        if (!bank) return null;
                        return (
                          <BankDetailsDisplay
                            key={bankId}
                            formData={{
                              bankName: bank.name,
                              bankingPartnerId: bank.id,
                              color: bank.color
                            }}
                            onDelete={async () => {
                              const confirm = window.confirm(`Are you sure you want to remove ${bank.name}?`);
                              if (!confirm || !applicationId) return;

                              try {
                                await MerchantApplicationService.removeBankFromApplication(
                                  applicationId,
                                  bankId
                                );

                                queryClient.invalidateQueries({
                                  queryKey: ['merchantApplication', applicationId]
                                });

                                toast({
                                  title: 'Success',
                                  description: `${bank.name} removed successfully`,
                                });
                              } catch (error) {
                                console.error('Error removing bank:', error);
                                toast({
                                  title: 'Error',
                                  description: 'Failed to remove bank',
                                  variant: 'destructive'
                                });
                              }
                            }}
                          />
                        );
                      })}

                      {/* Webhook Activity Log */}
                      {(application as any)?.lastWebhookEvent && (
                        <Card>
                          <CardHeader className="pb-2">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-gray-500" />
                              <h4 className="font-medium text-sm">Last Bank Update</h4>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Event:</span>
                                <span className="font-medium">{(application as any).lastWebhookEvent.eventType}</span>
                              </div>
                              {(application as any).lastWebhookEvent.receivedAt && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Received:</span>
                                  <span>
                                    {format(
                                      typeof (application as any).lastWebhookEvent.receivedAt?.toDate === 'function'
                                        ? (application as any).lastWebhookEvent.receivedAt.toDate()
                                        : new Date((application as any).lastWebhookEvent.receivedAt),
                                      'MMM d, yyyy h:mm a'
                                    )}
                                  </span>
                                </div>
                              )}
                              {(application as any).lastWebhookEvent.previousPipelineStatus && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Status Change:</span>
                                  <span>
                                    <Badge variant="outline" className="mr-1 text-xs">
                                      {(application as any).lastWebhookEvent.previousPipelineStatus}
                                    </Badge>
                                    →
                                    <Badge variant="outline" className="ml-1 text-xs bg-green-50 text-green-700">
                                      {application.pipelineStatus}
                                    </Badge>
                                  </span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Processing Analysis */}
                      <Card>
                        <CardHeader>
                          <h3 className="text-lg font-semibold">Processing Analysis</h3>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {application?.data?.processingHistory?.acceptOfflineCards && (
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="font-medium">Retail (In-Person)</Label>
                                <p className="text-2xl font-bold text-blue-600">
                                  {application.data.processingHistory.retailPercentage || 0}%
                                </p>
                              </div>
                              <div>
                                <Label className="font-medium">Online Sales</Label>
                                <p className="text-2xl font-bold text-green-600">
                                  {application.data.processingHistory.onlinePercentage || 0}%
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="border-t pt-4">
                            <Label className="font-medium mb-2 block">B2B/B2C Split</Label>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-sm text-muted-foreground">Business-to-Business</Label>
                                <p className="text-xl font-semibold">{application?.data?.processingHistory?.b2bPercentage || 50}%</p>
                              </div>
                              <div>
                                <Label className="text-sm text-muted-foreground">Business-to-Consumer</Label>
                                <p className="text-xl font-semibold">{application?.data?.processingHistory?.b2cPercentage || 50}%</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Risk Assessment */}
                      <Card>
                        <CardHeader>
                          <h3 className="text-lg font-semibold">Risk Assessment</h3>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="font-medium">Previous Terminations</Label>
                              <div className="flex items-center gap-2 mt-1">
                                {application?.data?.processingHistory?.hasBeenTerminated === 'yes' ? (
                                  <>
                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                    <span className="text-red-600 font-medium">Yes</span>
                                  </>
                                ) : (
                                  <>
                                    <Shield className="h-4 w-4 text-green-500" />
                                    <span className="text-green-600">No issues reported</span>
                                  </>
                                )}
                              </div>
                              {application?.data?.processingHistory?.terminationExplanation && (
                                <p className="text-sm text-muted-foreground mt-2">
                                  {application.data.processingHistory.terminationExplanation}
                                </p>
                              )}
                            </div>

                            <div>
                              <Label className="font-medium">Bankruptcy History</Label>
                              <div className="flex items-center gap-2 mt-1">
                                {application?.data?.processingHistory?.bankruptcy ? (
                                  <>
                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                    <span className="text-red-600 font-medium">Previously filed</span>
                                  </>
                                ) : (
                                  <>
                                    <Shield className="h-4 w-4 text-green-500" />
                                    <span className="text-green-600">No bankruptcy history</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="border-t pt-4">
                            <Label className="font-medium mb-2 block">Volume Analysis</Label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label className="text-sm text-muted-foreground">Monthly Volume</Label>
                                <p className="text-lg font-semibold">
                                  ${application?.data?.processingHistory?.monthlyVolume?.toLocaleString() || '0'}
                                </p>
                              </div>
                              <div>
                                <Label className="text-sm text-muted-foreground">Average Ticket</Label>
                                <p className="text-lg font-semibold">
                                  ${application?.data?.processingHistory?.averageTicket?.toLocaleString() || '0'}
                                </p>
                              </div>
                              <div>
                                <Label className="text-sm text-muted-foreground">High Ticket</Label>
                                <p className="text-lg font-semibold">
                                  ${application?.data?.processingHistory?.highTicket?.toLocaleString() || '0'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                )}

                {/* Processing Analytics Tab */}
                <TabsContent value="processing" className="mt-0 border-none p-0">
                  <ProcessingAnalyticsSection
                    companyId={application?.companyId || ''}
                    merchantName={application?.data?.corporationInfo?.businessName}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </Card>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Application</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this application? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => deleteApplicationMutation.mutate()}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Examine PayArc Payload Dialog */}
      <Dialog open={isExamineDialogOpen} onOpenChange={setIsExamineDialogOpen}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Examine PayArc API Payload</DialogTitle>
            <DialogDescription>
              Review the data that will be sent to PayArc. Errors and warnings are listed at the top.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 flex flex-col gap-4 overflow-hidden min-h-[500px]">
            {payArcPayloadPreview && (
              <>
                {/* Errors Section */}
                {payArcPayloadPreview.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex-shrink-0 max-h-[200px] flex flex-col">
                    <h3 className="font-semibold text-red-800 mb-2 flex items-center gap-2 flex-shrink-0">
                      <AlertTriangle className="w-4 h-4" />
                      Validation Errors ({payArcPayloadPreview.errors.length})
                    </h3>
                    <div className="overflow-y-auto flex-1 min-h-0">
                      <ul className="space-y-1 text-sm text-red-700">
                        {payArcPayloadPreview.errors.map((error, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-red-500 mt-0.5">•</span>
                            <span>{error}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Payload Section - Editable */}
                <div className="flex-1 flex flex-col min-h-[400px]">
                  <h3 className="font-semibold mb-2 flex-shrink-0">Payload JSON (editable)</h3>
                  <textarea
                    className="flex-1 w-full p-4 text-xs font-mono outline-none bg-gray-50 border rounded-lg resize-none min-h-[350px]"
                    value={editedPayloadText}
                    onChange={(e) => setEditedPayloadText(e.target.value)}
                    spellCheck={false}
                  />
                </div>
              </>
            )}
          </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => setIsExamineDialogOpen(false)}>
          Close
        </Button>
        <Button
              variant="outline"
              onClick={async () => {
                // Refresh from current data
                if (!application?.data) return;
                const { payload, errors } = await payarcService.generatePayload(application.data);
                setPayArcPayloadPreview({ payload, errors });
                setEditedPayloadText(JSON.stringify(payload, null, 2));
              }}
            >
              Refresh
            </Button>
            <Button
              onClick={async () => {
                try {
                  setIsSendingCustom(true);

                  // Find the PayArc banking partner ID (same logic as handleSendToPayArc)
                  const payArcBankId = application?.assignedBanks?.find(bankId => {
                    const bank = bankingPartners.find(p => p.id === bankId);
                    return bank?.name.toLowerCase().includes('payarc');
                  });

                  if (!payArcBankId) {
                    setIsSendingCustom(false);
                    toast({
                      title: 'PayArc Not Assigned',
                      description: 'Please assign PayArc as a banking partner first',
                      variant: 'destructive'
                    });
                    return;
                  }

                  // Parse edited JSON
                  const parsed = JSON.parse(editedPayloadText);
                  const response = await payarcService.sendRawLeadPayload(parsed, payArcBankId);

                  if (response.success) {
                    setPayArcResponse(response);

                    const successMessage = response.MerchantCode
                      ? `Application sent to PayArc. Merchant Code: ${response.MerchantCode}`
                      : 'Application sent to PayArc successfully.';

                    toast({
                      title: 'Success!',
                      description: successMessage,
                    });

                    // Save merchant code to local state and Firestore (same as handleSendToPayArc)
                    if (response.MerchantCode) {
                      setFormData((prev) => ({
                        ...prev,
                        payArcMerchantCode: response.MerchantCode,
                        payArcSubmittedAt: new Date(),
                      }));
                    }

                    // Update Firestore with merchant code
                    if (applicationId && response.MerchantCode) {
                      try {
                        const docRef = doc(db, 'merchantApplications', applicationId);
                        await updateDoc(docRef, {
                          'data.payArcMerchantCode': response.MerchantCode,
                          'data.payArcSubmittedAt': serverTimestamp(),
                          updatedAt: serverTimestamp(),
                        });
                      } catch (updateError) {
                        console.warn('Could not update PayArc merchant code:', updateError);
                      }
                    }

                    // Log the successful integration
                    await appendIntegrationLog({
                      source: 'payarc-lead',
                      status: 'success',
                      message: successMessage,
                      actor: 'crm-admin',
                      details: response?.data ? JSON.stringify(response.data, null, 2) : undefined,
                    });

                    // Invalidate query to refresh data
                    queryClient.invalidateQueries({ queryKey: ['merchantApplication', applicationId] });

                    setIsExamineDialogOpen(false);
                  } else {
                    const errorMessage = response.message || 'Failed to send application to PayArc';
                    toast({
                      title: 'PayArc Error',
                      description: errorMessage,
                      variant: 'destructive'
                    });
                    await appendIntegrationLog({
                      source: 'payarc-lead',
                      status: 'error',
                      message: errorMessage,
                      actor: 'crm-admin',
                      details: JSON.stringify(response.errors ?? response, null, 2),
                    });
                  }
                } catch (err: any) {
                  const message = err?.message || 'Please fix the JSON and try again';
                  toast({
                    title: 'Invalid JSON',
                    description: message,
                    variant: 'destructive'
                  });
                  await appendIntegrationLog({
                    source: 'payarc-lead',
                    status: 'error',
                    message: 'Failed to send custom payload',
                    actor: 'crm-admin',
                    details: err instanceof Error ? err.stack || err.message : JSON.stringify(err, null, 2),
                  });
                } finally {
                  setIsSendingCustom(false);
                }
              }}
              disabled={isPayArcSending || isSendingCustom}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Edited JSON
            </Button>
          </DialogFooter>
      </DialogContent>
    </Dialog>

      {/* Examine Submission Payload Dialog */}
      <Dialog open={isExamineSubmissionDialogOpen} onOpenChange={setIsExamineSubmissionDialogOpen}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Examine Submission API Payload</DialogTitle>
            <DialogDescription>
              Review the data that will be sent to PayArc for underwriting submission. Additional fields like apply_app_id and lead_code are fetched dynamically from PayArc's lead-status API during the actual submission.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 flex flex-col gap-4 overflow-hidden min-h-[500px]">
            {submissionPayloadPreview && (
              <>
                {/* Payload Section - Editable */}
                <div className="flex-1 flex flex-col min-h-[400px]">
                  <h3 className="font-semibold mb-2 flex-shrink-0">Submission Payload JSON (editable)</h3>
                  <textarea
                    className="flex-1 w-full p-4 text-xs font-mono outline-none bg-gray-50 border rounded-lg resize-none min-h-[350px]"
                    value={editedSubmissionPayloadText}
                    onChange={(e) => setEditedSubmissionPayloadText(e.target.value)}
                    spellCheck={false}
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExamineSubmissionDialogOpen(false)}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  // Refresh from current data - re-fetch lead-status
                  const refreshPayload: Record<string, unknown> = {
                    merchant_code: formData.payArcMerchantCode,
                    MerchantCode: formData.payArcMerchantCode,
                  };

                  // Fetch lead-status from PayArc again
                  try {
                    const leadStatusResponse = await payarcService.getLeadStatus(formData.payArcMerchantCode);

                    if (leadStatusResponse.success && leadStatusResponse.data) {
                      const rawStatus = leadStatusResponse.data as Record<string, any>;

                      // Helper function to find nested values
                      const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '');
                      const findNestedValue = (root: unknown, candidateKeys: string[]): unknown => {
                        const targetKeys = candidateKeys.map(normalizeKey);
                        const stack: unknown[] = [root];
                        const visited = new Set<unknown>();

                        while (stack.length > 0) {
                          const current = stack.pop();
                          if (!current || typeof current !== 'object') continue;
                          if (visited.has(current)) continue;
                          visited.add(current);

                          if (Array.isArray(current)) {
                            for (const item of current) {
                              if (item && typeof item === 'object') {
                                stack.push(item);
                              }
                            }
                            continue;
                          }

                          for (const [key, value] of Object.entries(current as Record<string, unknown>)) {
                            const normalized = normalizeKey(key);
                            if (targetKeys.includes(normalized) && (typeof value === 'string' || typeof value === 'number')) {
                              return value;
                            }

                            if (value && typeof value === 'object') {
                              stack.push(value);
                            }
                          }
                        }
                        return undefined;
                      };

                      // Extract apply_app_id
                      const applyAppId =
                        findNestedValue(rawStatus, [
                          'apply_app_id', 'applyappid', 'applyid', 'applicationid', 'applyapplicationid',
                        ]) ||
                        rawStatus.Apply_App_Id ||
                        rawStatus.apply_app_id ||
                        rawStatus.ApplyAppId ||
                        rawStatus.applyAppId ||
                        rawStatus?.Lead?.Apply_App_Id ||
                        rawStatus?.Lead?.apply_app_id ||
                        rawStatus.LeadCode ||
                        rawStatus.leadCode ||
                        rawStatus?.Lead?.LeadCode;

                      if (applyAppId !== undefined && applyAppId !== null && applyAppId !== '') {
                        const normalized = String(applyAppId);
                        refreshPayload.apply_app_id = normalized;
                        refreshPayload.applyAppId = normalized;
                      }

                      // Extract lead_code
                      const leadCode =
                        findNestedValue(rawStatus, [
                          'leadcode', 'lead_id', 'leadid', 'leadidentifier',
                        ]) ||
                        rawStatus.LeadCode ||
                        rawStatus.leadCode ||
                        rawStatus?.Lead?.LeadCode ||
                        rawStatus?.lead?.leadCode;

                      if (leadCode !== undefined && leadCode !== null && leadCode !== '') {
                        const normalized = String(leadCode);
                        refreshPayload.lead_code = normalized;
                        refreshPayload.LeadCode = normalized;
                      }
                    }
                  } catch (statusError) {
                    console.warn('Could not refresh lead-status:', statusError);
                  }

                  setSubmissionPayloadPreview({ payload: refreshPayload, errors: [] });
                  setEditedSubmissionPayloadText(JSON.stringify(refreshPayload, null, 2));
                } catch (err: any) {
                  toast({ title: 'Refresh Error', description: err?.message || 'Failed to refresh payload', variant: 'destructive' });
                }
              }}
            >
              Refresh
            </Button>
            <Button
              onClick={async () => {
                try {
                  setIsSendingCustomSubmission(true);
                  // Parse edited JSON and send the exact payload the user provided
                  const parsed = JSON.parse(editedSubmissionPayloadText);
                  // Send raw payload using the sendRawLeadPayload method
                  const resp = await payarcService.sendRawLeadPayload(parsed);
                  setIsSendingCustomSubmission(false);
                  setIsExamineSubmissionDialogOpen(false);
                  if (resp.success) {
                    toast({ title: 'Submitted to PayArc', description: 'Application submitted for underwriting successfully.' });
                  } else {
                    toast({ title: 'PayArc Error', description: resp.message || 'Failed to submit', variant: 'destructive' });
                  }
                } catch (err: any) {
                  setIsSendingCustomSubmission(false);
                  toast({ title: 'Invalid JSON', description: err?.message || 'Please fix the JSON and try again', variant: 'destructive' });
                }
              }}
              disabled={isSubmittingPayArc || isSendingCustomSubmission}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isLogDialogOpen}
        onOpenChange={(open) => {
          setIsLogDialogOpen(open);
          if (!open) {
            setSelectedLog(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogTitle>Integration Log Details</DialogTitle>
          {selectedLog ? (
            <div className="mt-4 space-y-4 text-sm">
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-slate-700">Message</span>
                <span className="text-slate-600">{selectedLog.message}</span>
              </div>
              <div className="flex flex-wrap gap-3 text-xs uppercase tracking-[0.12em] text-muted-foreground/80">
                <span>{selectedLog.source}</span>
                {selectedLog.actor && <span>· {selectedLog.actor}</span>}
                <span>
                  · {(() => {
                    try {
                      return format(new Date(selectedLog.timestamp), 'MMM d, yyyy h:mm a');
                    } catch {
                      return selectedLog.timestamp;
                    }
                  })()}
                </span>
              </div>
              <div className="max-h-[50vh] overflow-auto rounded border border-slate-200 bg-slate-50 p-4 font-mono whitespace-pre-wrap text-xs">
                {selectedLog.details || 'No additional details provided.'}
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-600">No log selected.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Business Description Edit Modal */}
      <Dialog
        open={isBusinessDescriptionModalOpen}
        onOpenChange={(open) => {
          setIsBusinessDescriptionModalOpen(open);
          if (!open) {
            setBusinessDescriptionValue('');
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Business Description</DialogTitle>
            <DialogDescription>
              Describe what your business does, the products or services you offer, and your target market.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="modal-business-description">Business Description</Label>
                <span className={`text-xs ${businessDescriptionValue.length > 500 ? 'text-red-500 font-medium' : businessDescriptionValue.length > 400 ? 'text-amber-500' : 'text-gray-400'}`}>
                  {businessDescriptionValue.length}/500
                </span>
              </div>
              <Textarea
                id="modal-business-description"
                value={businessDescriptionValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 500) {
                    setBusinessDescriptionValue(newValue);
                  }
                }}
                placeholder="Describe what your business does, the products or services you offer, and your target market..."
                rows={8}
                maxLength={500}
                className="resize-none"
              />
              {businessDescriptionValue.length > 500 && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Business description cannot exceed 500 characters
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsBusinessDescriptionModalOpen(false);
                setBusinessDescriptionValue('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (businessDescriptionValue.length <= 500) {
                  try {
                    // Update local form data
                    handleInputChange('corporationInfo.businessDescription', businessDescriptionValue);
                    
                    // Save to Firestore
                    await updateFieldMutation.mutateAsync({
                      field: 'corporationInfo.businessDescription',
                      value: businessDescriptionValue,
                    });
                    
                    // Close modal and reset state
                    setIsBusinessDescriptionModalOpen(false);
                    setBusinessDescriptionValue('');
                    
                    toast({
                      title: 'Business Description Updated',
                      description: 'The business description has been saved successfully.',
                    });
                  } catch (error) {
                    console.error('Error saving business description:', error);
                    toast({
                      title: 'Error',
                      description: 'Failed to save business description. Please try again.',
                      variant: 'destructive',
                    });
                  }
                }
              }}
              disabled={businessDescriptionValue.length > 500 || updateFieldMutation.isPending}
            >
              {updateFieldMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

